/**
 * Tipi del contract Gymmi API v1.
 *
 * Trascrizione a mano di `gymmi-contracts/openapi/gymmi-api.v1.yaml`, limitata a ciò
 * che il pannello consuma. Scritti a mano e non generati per due motivi:
 *
 * 1. i tipi generati da OpenAPI 3.1 con `oneOf: [X, null]` producono union verbose
 *    che poi vanno ristrette a ogni uso;
 * 2. il contract è stabile e versionato — quando cambia, il diff qui è il punto in
 *    cui si decide come reagire, invece di comparire come rumore in un file generato.
 *
 * Regola dal contract (docs/02-convenzioni.md): le aggiunte retrocompatibili
 * avvengono dentro v1, quindi **i campi sconosciuti si ignorano**, non si rifiuta la
 * risposta. Nessuno di questi tipi è quindi validato a runtime contro un allowlist.
 */

// ── primitive ──────────────────────────────────────────────────────────────────

/** UUID v4. */
export type Uuid = string
/** Timestamp RFC 3339 / ISO-8601 UTC con millisecondi: `2026-07-28T14:32:10.123Z`. */
export type IsoDateTime = string
/** Giorno civile, `2026-08-18`. Non è un istante: le analisi ragionano per giorni. */
export type IsoDate = string
/** Id di catalogo, prefissato per sorgente: `seed_`, `wx_`, `man_`. */
export type ExerciseId = string

export const MUSCLE_GROUP_KEYS = [
  'petto',
  'schiena',
  'spalle',
  'braccia',
  'core',
  'gambe',
  'glutei',
  'cardio',
  'altro',
] as const
export type MuscleGroupKey = (typeof MUSCLE_GROUP_KEYS)[number]

export const EXERCISE_LEVELS = ['beginner', 'intermediate', 'expert'] as const
export type ExerciseLevel = (typeof EXERCISE_LEVELS)[number]

export const EXERCISE_CATEGORIES = [
  'strength',
  'stretching',
  'plyometrics',
  'powerlifting',
  'olympic weightlifting',
  'strongman',
  'cardio',
] as const
export type ExerciseCategory = (typeof EXERCISE_CATEGORIES)[number]

export const EXERCISE_MECHANICS = ['compound', 'isolation'] as const
export type ExerciseMechanic = (typeof EXERCISE_MECHANICS)[number]

export const EXERCISE_FORCES = ['pull', 'push', 'static'] as const
export type ExerciseForce = (typeof EXERCISE_FORCES)[number]

export const EXERCISE_SOURCES = ['seed', 'workoutx', 'manual'] as const
export type ExerciseSource = (typeof EXERCISE_SOURCES)[number]

export const USER_ROLES = ['user', 'admin'] as const
export type UserRole = (typeof USER_ROLES)[number]

export type Sex = 'male' | 'female' | 'other'
export type ThemeMode = 'light' | 'dark' | 'system'

/**
 * Ruoli **dentro un'organizzazione**, distinti da `UserRole` che è il ruolo di
 * piattaforma.
 *
 * Sono due assi diversi e vanno tenuti separati anche nei tipi: `admin` dice che
 * l'account amministra il prodotto (catalogo, import, moderazione), `owner` dice
 * che comanda in *una* palestra. Confonderli produrrebbe una UI che offre azioni
 * di piattaforma a un titolare, o viceversa.
 */
export const ORG_ROLES = [
  'owner',
  'manager',
  'receptionist',
  'trainer',
  'nutritionist',
  'member',
] as const
export type OrgRole = (typeof ORG_ROLES)[number]

export const MEMBERSHIP_STATUSES = ['invited', 'active', 'suspended'] as const
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number]

/** `personal` è l'area che ogni account ha comunque; `gym` è una palestra vera. */
export type OrganizationKind = 'personal' | 'gym'

// ── errori ─────────────────────────────────────────────────────────────────────

/**
 * Envelope di errore, identico su ogni endpoint (docs/05-errori.md).
 * `code` è stabile e i client ramificano su quello; `message` è già in italiano e
 * mostrabile, e può cambiare senza rottura di contract.
 */
export interface ApiErrorEnvelope {
  error: {
    code: string
    message: string
    details?: Record<string, unknown> | null
    requestId?: string
  }
}

/** Errore per campo di `validation_failed`, in `details.fields`. */
export interface FieldError {
  /** Notazione a punti, con indice per gli array: `exercises[3].sets`. */
  field: string
  code: string
  message: string
}

// ── auth ───────────────────────────────────────────────────────────────────────

export interface User {
  id: Uuid
  email: string
  role: UserRole
  emailVerified?: boolean
  /** Stato esplicito del server: non va ridedotto dal profilo. */
  onboardingCompleted: boolean
  createdAt: IsoDateTime
  updatedAt?: IsoDateTime
}

export interface AuthSession {
  accessToken: string
  refreshToken: string
  tokenType: 'Bearer'
  /** Durata dell'access token in secondi (900 = 15 minuti). */
  expiresIn: number
  user: User
}

export interface LoginRequest {
  email: string
  password: string
}

/** Claim dell'access token che il pannello legge (docs/03-autenticazione.md). */
export interface AccessTokenClaims {
  sub: Uuid
  email?: string
  role?: UserRole
  /** Organizzazione con cui la sessione stava lavorando (claim `org`, TEN-04). */
  org?: Uuid
  /** Scadenza in secondi epoch. */
  exp: number
  iat?: number
  jti?: string
}

// ── organizzazioni e appartenenze (TEN-06) ─────────────────────────────────────

/**
 * Una delle organizzazioni a cui l'account appartiene — `OrganizationMembership`
 * del contract, campo per campo.
 *
 * È ciò che alimenta il selettore: l'id finisce nell'header `X-Organization-Id`,
 * il nome è l'unica cosa che l'operatore riconosce.
 *
 * Ciò che **non** c'è è deliberato quanto ciò che c'è. Niente `status`: `/me`
 * elenca le sole appartenenze attive (il server filtra in SQL), quindi il campo
 * varrebbe la costante `active` e chi lo leggesse crederebbe di poter distinguere
 * qualcosa. Niente sedi e niente anagrafica dell'organizzazione: `/me` è
 * autenticata e nient'altro — deve riuscire anche con una selezione conservata
 * che non vale più — quindi tutto ciò che le si appende lo vede ogni iscritto di
 * ogni palestra. Qui sta la **relazione** dell'account con le sue organizzazioni,
 * non i dati delle organizzazioni.
 */
export interface Membership {
  organizationId: Uuid
  organizationName: string
  organizationKind: OrganizationKind
  /**
   * Riga di `organization_members`: identifica l'**appartenenza**, non l'utente.
   *
   * È la chiave con cui `/organization/members/{memberId}` nomina le persone, ed
   * è ciò che permette di riconoscere la propria riga fra gli operatori.
   *
   * Facoltativo perché il contract lo dichiara tale finché il backend che lo
   * emette non è in produzione: dichiararlo obbligatorio qui sarebbe la stessa
   * bugia che questa revisione ripara — un tipo che promette ciò che il server
   * può non mandare. Quando manca, nessuna riga risulta la propria e «(tu)» non
   * compare: si perde un'etichetta, non si sbaglia persona.
   */
  memberId?: Uuid
  role: OrgRole
  /**
   * Nome del ruolo personalizzato quando ne sostituisce uno predefinito, `null`
   * altrimenti — le etichette dei sei ruoli di serie le mette il pannello.
   *
   * È il nome, non il ruolo: `role` resta quello di partenza anche quando i
   * permessi vengono da altrove. Scriverlo al posto dell'etichetta predefinita
   * evita di chiamare "Reception" una persona a cui la palestra ha dato di
   * proposito il nome "Reception con cassa".
   */
  customRoleLabel?: string | null
  /**
   * Permessi **effettivi** in questa organizzazione, già risolti dal server
   * (OPS-01): quelli del ruolo predefinito, oppure quelli del ruolo
   * personalizzato quando ce n'è uno — sostituiti, non uniti.
   *
   * Servono a **nascondere** un'azione invece di proporla e poi rifiutarla con un
   * `403`. Non sono un confine di sicurezza: quello resta sul server, su ogni
   * richiesta; qui è un dato vecchio fino a cinque minuti (`staleTime` di
   * `useMemberships`) e non deve mai *concedere* niente.
   */
  permissions: string[]
  /**
   * `true` per l'organizzazione con cui **questa** richiesta ha lavorato, ed
   * esattamente un elemento dell'elenco ce l'ha.
   *
   * È il verdetto della cascata lato server, non la scelta dell'operatore: il
   * pannello chiama `/me` senza `X-Organization-Id` (vedi `listMemberships`),
   * quindi qui vale "l'organizzazione per cui il token è stato emesso". La
   * selezione dell'operatore, quando c'è, ha la precedenza — sostituire l'una con
   * l'altra mostrerebbe un nome sopra dati che arrivano da un'altra palestra.
   */
  current: boolean
}

/**
 * Operatore di un'organizzazione — `MemberDto` di `members.service.ts`.
 *
 * `memberId` e `userId` sono cose diverse e servono entrambi: le rotte
 * `/organization/members/{id}` vogliono l'**appartenenza**, mentre l'account è
 * ciò che si vede in `/utenti`. Confonderli produce `404` che sembrano permessi
 * mancanti.
 */
export interface Member {
  memberId: Uuid
  userId: Uuid
  email: string
  name: string | null
  role: OrgRole
  customRoleId: Uuid | null
  status: MembershipStatus
  /** **Elenco vuoto = tutte le sedi**: è la convenzione dello schema, non un dato mancante. */
  locationIds: Uuid[]
  /** Permessi effettivi già risolti dal server: ruolo predefinito o ruolo personalizzato. */
  permissions: string[]
  joinedAt: IsoDateTime | null
  createdAt: IsoDateTime
}

/** Invito aperto — `InvitationDto`. Il token non esce mai: viaggia solo nell'email. */
export interface Invitation {
  invitationId: Uuid
  email: string
  role: OrgRole
  customRoleId: Uuid | null
  locationIds: Uuid[]
  status: 'pending' | 'accepted' | 'revoked' | 'expired'
  expiresAt: IsoDateTime
  createdAt: IsoDateTime
}

export interface InviteMemberInput {
  email: string
  role?: OrgRole
  customRoleId?: Uuid
  locationIds?: Uuid[]
}

export interface UpdateMemberInput {
  role?: OrgRole
  /** `null` toglie il ruolo personalizzato e riporta a quello predefinito. */
  customRoleId?: Uuid | null
  status?: MembershipStatus
  /** Motivo della sospensione: annotazione interna, non mostrata all'interessato. */
  reason?: string
  locationIds?: Uuid[]
}

// ── verifica in due passaggi (OPS-04) ──────────────────────────────────────────

export interface MfaStatus {
  enabled: boolean
  /** Segreto generato ma non confermato: la configurazione è a metà, il 2FA **non** è attivo. */
  pending: boolean
  recoveryCodesRemaining: number
}

export interface MfaSetup {
  /** Base32, da digitare a mano quando il QR non si può inquadrare. */
  secret: string
  /** `otpauth://totp/...`, il contenuto che un QR codificherebbe. */
  otpauthUri: string
}

/** I codici di recupero escono **una volta sola**, alla conferma. */
export interface MfaConfirmResult {
  recoveryCodes: string[]
}

/**
 * Buono emesso dal login quando manca il secondo fattore.
 *
 * Non è una sessione: vale due minuti, è monouso e non apre nulla. Fra password
 * e codice non esiste un access token, per non mettere in circolazione un token
 * valido a metà autenticazione.
 */
export interface MfaChallenge {
  mfaToken: string
  expiresIn: number
}

/**
 * `GET /me`. Il pannello ne usa oggi solo `user` e `memberships`.
 *
 * `memberships` è **obbligatorio e mai vuoto**: ogni account ha almeno la propria
 * area personale, e una richiesta autenticata senza appartenenze utilizzabili non
 * arriva a `200` — risponde `403 organization_forbidden`. Dichiararlo facoltativo
 * inviterebbe a dare un significato all'assenza ("non appartieni a nulla") che
 * l'assenza non ha: significa solo che la risposta è rotta. Il controllo a runtime
 * in `sanitizeMemberships` resta comunque, e non è sfiducia nel contract: sulla
 * rotta non c'è uno schema di risposta lato server, quindi la forma non è
 * verificata da nessuno dei due lati del filo.
 *
 * Qui **non** arriva un'organizzazione risolta a parte: quale sia l'organizzazione
 * della richiesta lo dice `current` dentro l'elenco. Un oggetto accanto all'array
 * potrebbe nominarne una che nell'array non c'è, e allora il pannello dovrebbe
 * decidere a chi credere.
 */
export interface MeResponse {
  user: User
  profile?: UserProfile | null
  settings?: UserSettings
  memberships: Membership[]
  catalogVersion?: string
  catalogCount?: number
}

// ── profilo ────────────────────────────────────────────────────────────────────

export interface UserProfile {
  name: string
  age?: number | null
  sex: Sex
  weightKg?: number | null
  heightCm?: number | null
  updatedAt: IsoDateTime
}

export interface UserSettings {
  themeMode?: ThemeMode
  updatedAt?: IsoDateTime
}

// ── catalogo ───────────────────────────────────────────────────────────────────

export interface Exercise {
  id: ExerciseId
  name: string
  /** Nomi grezzi (`lats`, `chest`), risolvibili con `/reference/muscles`. */
  primaryMuscles: string[]
  secondaryMuscles: string[]
  /** Passi in italiano, uno per elemento. */
  instructions: string[]
  /** URL https assoluti, in ordine di preferenza. */
  images: string[]
  equipment?: string | null
  category?: ExerciseCategory | null
  level?: ExerciseLevel | null
  mechanic?: ExerciseMechanic | null
  force?: ExerciseForce | null
  /** Solo sui dati WorkoutX; fallback per la risoluzione del gruppo. */
  bodyPart?: string | null
  /** GIF WorkoutX. L'app non la mostra (watermark sul piano free). */
  gifUrl?: string | null
  source: ExerciseSource
  /** Derivato dal server dal muscolo primario. Read-only. */
  group?: MuscleGroupKey
  updatedAt?: IsoDateTime
}

/** `Exercise` più i campi editoriali visibili solo agli admin. */
export interface AdminExercise extends Exercise {
  published: boolean
  /** Valorizzato dall'eliminazione soft. */
  deletedAt?: IsoDateTime | null
  /** Id nella sorgente originale, prima della prefissazione. */
  externalId?: string | null
  lastImportJobId?: Uuid | null
  /**
   * Numero di schede utente che citano questo esercizio. Va mostrato **prima** di
   * ogni eliminazione: è la differenza fra rimuovere un duplicato e rompere le
   * schede di trecento persone.
   */
  cardUsageCount?: number
}

export interface ExerciseInput {
  /** Obbligatorio negli upsert in blocco. In creazione il server genera `man_<slug>`. */
  id?: ExerciseId
  name: string
  primaryMuscles: string[]
  secondaryMuscles?: string[]
  instructions?: string[]
  images?: string[]
  equipment?: string | null
  category?: ExerciseCategory | null
  level?: ExerciseLevel | null
  mechanic?: ExerciseMechanic | null
  force?: ExerciseForce | null
  bodyPart?: string | null
  gifUrl?: string | null
  source?: ExerciseSource
  published?: boolean
}

export type ExercisePatch = Partial<Omit<ExerciseInput, 'id' | 'source'>>

export interface BulkUpsertError {
  /** Posizione della riga nell'array inviato. */
  index: number
  id?: ExerciseId | null
  code: string
  message: string
}

/**
 * Esito riga per riga. L'operazione **non** è atomica: le righe valide passano
 * anche se altre falliscono.
 */
export interface BulkUpsertResult {
  dryRun: boolean
  created: number
  updated: number
  /** Righe identiche a quelle già in catalogo, non riscritte. */
  unchanged: number
  failed: number
  catalogVersionAfter?: string | null
  errors?: BulkUpsertError[]
}

// ── reference ──────────────────────────────────────────────────────────────────

export interface MuscleGroup {
  key: MuscleGroupKey
  label: string
  /** Esadecimale maiuscolo, `#RRGGBB`. */
  color: string
  order: number
}

export interface MuscleDictionaryEntry {
  raw: string
  /** Slug della mappa corporea; `null` se non disegnabile sul corpo. */
  canonical: string | null
  group: MuscleGroupKey
  labelIt: string
}

export interface MuscleReference {
  groups: MuscleGroup[]
  entries: MuscleDictionaryEntry[]
  canonicalSlugs: string[]
}

export interface ExerciseAlias {
  alias: string
  exerciseName: string
  /** `null` se il nome di destinazione non è (più) in catalogo. */
  exerciseId?: ExerciseId | null
}

// ── schede ─────────────────────────────────────────────────────────────────────

export interface CardExercise {
  id: Uuid
  exerciseId: ExerciseId
  /** Denormalizzato dal server: la scheda resta leggibile anche fuori catalogo. */
  exerciseName: string
  /**
   * Settimana e giorno di **programma** — TRN-03, contract `1.0.0-draft.85`.
   *
   * `1`/`1` su ogni riga è la scheda classica, cioè un foglio unico. L'elenco
   * arriva ordinato per settimana, giorno e posizione, quindi le righe di uno
   * stesso giorno sono **contigue**: si raggruppano scorrendo l'array una volta.
   *
   * Facoltativi in questo tipo e non nel contract: il pannello legge anche i
   * finti, e un campo obbligatorio qui avrebbe costretto a toccare ogni fixture
   * per una cosa che il default risolve.
   */
  weekNumber?: number
  dayNumber?: number
  sets: number
  /** Testo libero — `8-12`, `AMRAP`, `30s`. */
  reps: string
  weight?: string | null
  restSeconds?: number | null
  note?: string | null
}

export interface WorkoutCard {
  id: Uuid
  name: string
  note?: string | null
  /** Colore accento ARGB 32 bit, come `Color.value` in Flutter. Il pannello non lo mostra. */
  colorValue?: number | null
  /** L'indice dell'array **è** l'ordine di esecuzione. */
  exercises: CardExercise[]
  exerciseCount?: number
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

// ── admin: utenti ──────────────────────────────────────────────────────────────

export interface AdminUser extends User {
  disabled?: boolean
  /** Annotazione interna, mai mostrata all'utente finale. */
  disabledReason?: string | null
  profile?: UserProfile | null
  cardCount?: number
  lastLoginAt?: IsoDateTime | null
  // Qui non c'è un `authProvider`, e non è una dimenticanza: il contract ha smesso di
  // esporlo. Email e password sono l'unico modo di autenticarsi, quindi il campo avrebbe
  // un solo valore su ogni utente e il pannello non potrebbe ramificarci sopra. Se un
  // secondo provider di identità arriverà davvero, rimetterlo sarà un'aggiunta
  // compatibile; tenerlo adesso sarebbe promettere per sempre un dato che non varia.
}

export interface AdminUserPatch {
  role?: UserRole
  disabled?: boolean
  disabledReason?: string
}

// ── admin: metriche ────────────────────────────────────────────────────────────

export interface AdminStats {
  users?: {
    total?: number
    onboarded?: number
    disabled?: number
    newLast7Days?: number
  }
  cards?: {
    total?: number
    averagePerUser?: number
  }
  catalog?: {
    total?: number
    published?: number
    bySource?: Partial<Record<ExerciseSource, number>> & Record<string, number>
    catalogVersion?: string
  }
  coach?: {
    sessionsLast7Days?: number
    cardsCreatedLast7Days?: number
    /**
     * Quota di bozze rimaste `unmatched`, da 0 a 1. È la metrica di salute del
     * Coach: se sale, catalogo o tabella alias sono da aggiornare.
     */
    unmatchedRateLast7Days?: number
  }
}

// ── admin: import ──────────────────────────────────────────────────────────────

export const IMPORT_SOURCES = ['workoutx', 'seed_file'] as const
export type ImportSource = (typeof IMPORT_SOURCES)[number]

export const IMPORT_JOB_STATUSES = [
  'queued',
  'running',
  'succeeded',
  'failed',
  'cancelled',
] as const
export type ImportJobStatus = (typeof IMPORT_JOB_STATUSES)[number]

export type ImportMode = 'upsert' | 'replace'

export interface CreateImportRequest {
  source: ImportSource
  mode?: ImportMode
  dryRun?: boolean
  /** `false` importa senza esporre: il batch si rivede e si pubblica dopo. */
  publish?: boolean
  maxItems?: number
}

export interface ImportJobWarning {
  code: string
  message: string
  externalId?: string | null
}

export interface ImportJobProgress {
  fetched: number
  created: number
  updated: number
  /** Invariati o esclusi dai filtri. */
  skipped: number
  failed: number
  /** Solo in modalità `replace`. */
  softDeleted?: number
  /**
   * Totale dichiarato dalla sorgente. WorkoutX non lo espone sempre, quindi può
   * restare `null` per tutta la durata del job: la UI deve gestire un avanzamento
   * indeterminato (docs/08-api-admin.md).
   */
  total?: number | null
}

export interface ImportJob {
  id: Uuid
  source: ImportSource
  mode: ImportMode
  status: ImportJobStatus
  dryRun: boolean
  progress: ImportJobProgress
  warnings?: ImportJobWarning[]
  error?: { code: string; message: string } | null
  createdBy?: Uuid
  createdAt: IsoDateTime
  startedAt?: IsoDateTime | null
  finishedAt?: IsoDateTime | null
  catalogVersionAfter?: string | null
}

/** Job in uno stato terminale: non più interrogabile né annullabile. */
export const TERMINAL_IMPORT_STATUSES: readonly ImportJobStatus[] = [
  'succeeded',
  'failed',
  'cancelled',
]

export function isImportRunning(job: Pick<ImportJob, 'status'>): boolean {
  return job.status === 'queued' || job.status === 'running'
}

// ── registro delle azioni (OPS-06) ─────────────────────────────────────────────

/**
 * Riga del registro.
 *
 * `organizationId` è `null` per le azioni **di piattaforma** — import del
 * catalogo, moderazione degli account: non avvengono dentro una palestra, e
 * attribuirle a una sarebbe una registrazione falsa. È anche il motivo per cui
 * quelle righe non compaiono in `GET /audit`, dove RLS filtra per tenant.
 *
 * `actorId` è `null` quando l'account è stato cancellato dopo l'azione
 * (`ON DELETE SET NULL`): `actorEmail` sopravvive, ed è l'unica traccia di chi
 * fosse.
 */
export interface AuditEntry {
  id: Uuid
  organizationId: Uuid | null
  actorId: Uuid | null
  actorEmail: string | null
  /** Forma `dominio.azione`: `exercise.update`, `member.role_change`. */
  action: string
  targetType: string | null
  /** `text` e non uuid: il bersaglio di un'azione sul catalogo è un id di esercizio. */
  targetId: string | null
  ip: string | null
  userAgent: string | null
  requestId: string | null
  createdAt: IsoDateTime
}

/**
 * Dettaglio, con il valore prima e dopo.
 *
 * Non escono nell'elenco di proposito: il payload è il valore *precedente di una
 * riga intera* — sull'utente contiene email e stato, sul catalogo interi array di
 * istruzioni. Guardarlo deve essere un'operazione deliberata, non un effetto
 * collaterale dello scorrimento.
 *
 * `unknown` e non un tipo: la forma dipende dall'azione, e le chiavi sensibili
 * sono già redatte in scrittura dal server.
 */
export interface AuditEntryDetail extends AuditEntry {
  before: unknown
  after: unknown
}

/**
 * Quale registro si sta guardando.
 *
 * Non è una preferenza di visualizzazione, sono due endpoint con due
 * autorizzazioni diverse: `platform` (`GET /admin/audit`, `platform_admin`) vede
 * tutto comprese le azioni senza organizzazione; `organization` (`GET /audit`,
 * permesso `audit:read`) vede solo il tenant della richiesta.
 */
export const AUDIT_SCOPES = ['platform', 'organization'] as const
export type AuditScope = (typeof AUDIT_SCOPES)[number]

// ── ordinamento (OPS-07) ───────────────────────────────────────────────────────

export const SORT_ORDERS = ['asc', 'desc'] as const
export type SortOrder = (typeof SORT_ORDERS)[number]

/**
 * Campi ordinabili di `GET /admin/users`.
 *
 * `lastLoginAt` è il motivo per cui il parametro esiste: «chi non entra da tre
 * mesi» non è una domanda a cui si risponde riordinando una pagina di
 * venticinque righe su cinquemila.
 */
export const USER_SORTS = ['createdAt', 'email', 'lastLoginAt'] as const
export type UserSort = (typeof USER_SORTS)[number]

/**
 * Campi ordinabili di `GET /admin/exercises`.
 *
 * `createdAt` **non** c'è, ed è una scelta del backend: per un catalogo
 * importato in blocco la data di creazione è l'ordine dell'import, cioè
 * un'informazione che non risponde a nessuna domanda.
 */
export const EXERCISE_SORTS = ['name', 'updatedAt'] as const
export type ExerciseSort = (typeof EXERCISE_SORTS)[number]

// ── paginazione ────────────────────────────────────────────────────────────────

export interface PageMeta {
  /** `null` sull'ultima pagina. È l'**unica** condizione di fine da usare. */
  nextCursor: string | null
  /** Presente solo quando calcolabile senza costo eccessivo. */
  total?: number
}

export interface Page<T> {
  data: T[]
  meta: PageMeta
}

// ── system ─────────────────────────────────────────────────────────────────────

export type HealthStatus = 'ok' | 'degraded' | 'unavailable'

export interface Readiness {
  status: HealthStatus
  version?: string
  checks: {
    name: string
    status: HealthStatus
    latencyMs?: number
    message?: string | null
  }[]
}

// ── F2: iscritti, iscrizioni, accessi ──────────────────────────────────────────
//
// ⚠️ **`GymMember` non è `OrganizationMember`.** Il secondo è un **operatore**:
// una relazione fra un account e la palestra, con un ruolo e dei permessi. Il
// primo è un **iscritto**: il cliente, che nella maggioranza dei casi non ha e non
// avrà mai un account nell'app. I due percorsi differiscono di una parola —
// `/organization/members` e `/organization/gym-members` — e chiamare l'uno
// credendo di chiamare l'altro non produce un errore: produce un elenco vuoto.

export const GYM_MEMBER_STATUSES = ['active', 'suspended', 'ended'] as const
export type GymMemberStatus = (typeof GYM_MEMBER_STATUSES)[number]

export const CONTACT_KINDS = ['phone', 'email', 'other'] as const
export type ContactKind = (typeof CONTACT_KINDS)[number]

export const CONSENT_KINDS = ['data_processing', 'marketing', 'image', 'third_party'] as const
export type ConsentKind = (typeof CONSENT_KINDS)[number]

export const CONSENT_CHANNELS = ['paper', 'app', 'panel', 'web'] as const
export type ConsentChannel = (typeof CONSENT_CHANNELS)[number]

export interface GymMemberContact {
  id: Uuid
  kind: ContactKind
  value: string
  label: string | null
  isEmergency: boolean
  createdAt: string
}

export interface GymMemberRelation {
  id: Uuid
  relatedGymMemberId: Uuid | null
  relatedName: string | null
  relation: 'parent' | 'guardian' | 'child' | 'spouse' | 'sibling' | 'other'
  isLegalGuardian: boolean
  externalPhone: string | null
  externalEmail: string | null
  createdAt: string
}

export interface GymMemberConsent {
  id: Uuid
  kind: ConsentKind
  granted: boolean
  policyVersion: string
  channel: ConsentChannel
  collectedByName: string | null
  note: string | null
  occurredAt: string
  createdAt: string
}

export interface GymMemberSportCard {
  id: Uuid
  body: string
  number: string
  issuedOn: string | null
  expiresOn: string | null
  createdAt: string
}

/**
 * La riga di elenco. **Deliberatamente priva di dati identificativi**: niente
 * codice fiscale, nascita, residenza, contatti o note interne. Non è `GymMember`
 * con dei campi in meno per via dei permessi — è uno schema diverso, e la
 * conseguenza per la UI è che non esiste una vista tabellare con la colonna del
 * codice fiscale: quella si vede aprendo una scheda.
 */
export interface GymMemberListItem {
  id: Uuid
  memberNumber: string | null
  firstName: string
  lastName: string
  status: GymMemberStatus
  hasPhoto: boolean
  anonymizedAt: string | null
  /**
   * La sede di riferimento **per nome**, e sta nella riga benché la riga sia priva di dati
   * identificativi: una sede non identifica nessuno — dice a quale palestra del gruppo
   * appartiene una persona. Senza il nome qui, un elenco filtrato per sede sarebbe un elenco
   * più corto senza una spiegazione.
   *
   * `null` è «di nessuna sede», non «di tutte»: in una palestra con una sede sola è il valore
   * di tutti, e va detto invece di sembrare un dato mancante.
   */
  primaryLocationName: string | null
  createdAt: string
}

export interface GymMember extends GymMemberListItem {
  userId: Uuid | null
  bornOn: string | null
  birthPlace: string | null
  birthProvince: string | null
  birthCountryCode: string | null
  sex: Sex | null
  taxCode: string | null
  email: string | null
  phone: string | null
  addressLine: string | null
  postalCode: string | null
  city: string | null
  province: string | null
  countryCode: string
  /**
   * La sede di riferimento — ANA-01, TEN-06.
   *
   * ⚠️ «Di riferimento» non è «unica»: il **motore d'accesso non guarda questo campo**, e chi
   * ha un abbonamento open entra in tutte le sedi comunque. Serve a sapere di chi è un
   * iscritto — a chi telefona, chi lo segue, quale scadenzario lo mostra — e un'interfaccia
   * che lo presentasse come un permesso d'ingresso prometterebbe una cosa che non c'è.
   */
  primaryLocationId: Uuid | null
  /**
   * ⚠️ È il campo da cui i dati sanitari rientrano dalla finestra («ginocchio
   * operato»), e sono categoria particolare ex art. 9 GDPR mentre DEC-04 è aperta.
   * Un'interfaccia che lo espone deve avvertire **accanto alla casella di testo**.
   */
  internalNotes: string | null
  contacts: GymMemberContact[]
  relations: GymMemberRelation[]
  currentConsents: GymMemberConsent[]
  currentSportCard: GymMemberSportCard | null
  /** Array e non campo singolo: due iscrizioni attive insieme sono normali. */
  activeSubscriptions: GymSubscription[]
  updatedAt: string
}

export const GYM_SUBSCRIPTION_KINDS = ['time', 'entries', 'mixed', 'trial'] as const
export type GymSubscriptionKind = (typeof GYM_SUBSCRIPTION_KINDS)[number]

export const GYM_SUBSCRIPTION_STATUSES = ['active', 'suspended', 'expired', 'cancelled'] as const
export type GymSubscriptionStatus = (typeof GYM_SUBSCRIPTION_STATUSES)[number]

export interface GymSubscriptionAccessWindow {
  weekday: number
  from: string
  to: string
}

export interface GymSuspension {
  id: Uuid
  startsOn: string
  endsOn: string | null
  reason: 'injury' | 'holiday' | 'medical' | 'other'
  note: string | null
  /** Giorni di proroga già applicati alla scadenza. Li conta il server. */
  daysGranted: number
  createdByName: string | null
  createdAt: string
}

export interface GymSubscription {
  id: Uuid
  gymMemberId: Uuid
  locationId: Uuid | null
  productId: Uuid | null
  productName: string
  kind: GymSubscriptionKind
  startsOn: string
  /** `null` solo su un carnet puro: finisce quando finiscono gli ingressi. */
  endsOn: string | null
  entriesTotal: number | null
  entriesUsed: number
  entriesRemaining: number | null
  accessWindow: GymSubscriptionAccessWindow[]
  autoRenew: boolean
  status: GymSubscriptionStatus
  /** **Centesimi interi.** Mai un decimale in virgola mobile per farci conti. */
  priceCents: number
  discountCents: number
  currency: string
  registeredByName: string | null
  previousGymSubscriptionId: Uuid | null
  renewedByGymSubscriptionId: Uuid | null
  note: string | null
  suspensions: GymSuspension[]
  createdAt: string
  updatedAt: string
}

/** La riga dello scadenzario: porta i dati per **telefonare** a qualcuno. */
export interface GymSubscriptionListItem {
  id: Uuid
  gymMemberId: Uuid
  gymMemberFirstName: string
  gymMemberLastName: string
  gymMemberPhone: string | null
  productName: string
  kind: GymSubscriptionKind
  startsOn: string
  endsOn: string | null
  entriesRemaining: number | null
  status: GymSubscriptionStatus
  /** Se **un'altra** iscrizione la nomina come precedente. Lo calcola il server. */
  renewed: boolean
  autoRenew: boolean
  priceCents: number
  currency: string
  createdAt: string
}

export const ACCESS_METHODS = ['qr', 'printed', 'manual', 'turnstile'] as const
export type AccessMethod = (typeof ACCESS_METHODS)[number]

export const ACCESS_DENIED_REASONS = [
  'unknown_credential',
  'member_suspended',
  'no_subscription',
  'subscription_expired',
  'no_entries_left',
  'outside_access_window',
  'location_full',
  'sport_card_expired',
  'anti_passback',
  // SRV-02: compaiono solo su un varco che nomina un servizio.
  'service_not_included',
  'outside_service_window',
  'service_monthly_limit_reached',
  'service_unavailable',
  // SRV-04: il carnet del servizio, che non e' quello dell'abbonamento.
  'no_service_credits',
] as const
export type AccessDeniedReason = (typeof ACCESS_DENIED_REASONS)[number]

export interface AccessEvent {
  id: Uuid
  gymMemberId: Uuid | null
  locationId: Uuid | null
  occurredAt: string
  direction: 'in' | 'out'
  outcome: 'allowed' | 'denied'
  deniedReason: AccessDeniedReason | null
  method: AccessMethod
  gate: string | null
  operatorName: string | null
  /** La sua presenza è il segno che l'esito non è quello che il motore aveva deciso. */
  overrideReason: string | null
  gymSubscriptionId: Uuid | null
  /** SRV-02: il servizio a cui il passaggio dava accesso, `null` in palestra. */
  serviceId: Uuid | null
  serviceName: string | null
  autoClosed: boolean
  createdAt: string
}

export interface AccessGymMemberSummary {
  id: Uuid
  firstName: string
  lastName: string
  status: GymMemberStatus
  hasPhoto: boolean
}

export interface AccessSubscriptionSummary {
  id: Uuid
  kind: GymSubscriptionKind
  productName: string
  status: GymSubscriptionStatus
  endsOn: string | null
  entriesTotal: number | null
  entriesUsed: number
  entriesRemaining: number | null
}

/**
 * L'esito di un tentativo. **Un rifiuto è un `201`**: la UI legge `event.outcome`
 * e non lo stato HTTP — un `catch` che mostra «errore di rete» su un accesso
 * negato è il modo più facile di sbagliare la postazione della reception.
 */
export interface AccessAttemptResult {
  event: AccessEvent
  gymMember: AccessGymMemberSummary | null
  subscription: AccessSubscriptionSummary | null
}

/**
 * Una persona che risulta dentro — ACC-04.
 *
 * Da `draft.88` vale anche l'uscita presunta della sede (ACC-07): chi è entrato da più di
 * `Location.presumedExitAfterMinutes` senza un'uscita registrata non compare più qui, e lo
 * conta `LocationOccupancy.presumedOut`.
 */
export interface PresenceEntry {
  gymMemberId: Uuid
  firstName: string
  lastName: string
  locationId: Uuid | null
  since: string
  gate: string | null
}

// ── affollamento (ACC-07, contract `1.0.0-draft.88`) ──────────────────────────
//
// Non è un dato nuovo: è la presenza di ACC-04 **contata** sede per sede contro la capienza.
// Il numero di `LocationOccupancy.inside` è quello di `GET /organization/presence` ed è quello
// con cui il varco decide `location_full` — una definizione sola, perché tre schermate che
// contassero «chi è dentro» in tre modi darebbero tre numeri, e la reception smetterebbe di
// credere a tutti e tre.

/**
 * Il numero in una parola, con soglie scritte nel contract e non configurabili: `low` sotto il
 * 40% del riferimento, `medium` fino al 75%, `high` oltre. `full` è di un'altra specie: la
 * capienza raggiunta, cioè il varco che **sta già rifiutando** con `location_full` — l'unico
 * livello che descrive una conseguenza e non un'impressione.
 */
export const OCCUPANCY_LEVELS = ['low', 'medium', 'high', 'full'] as const
export type OccupancyLevel = (typeof OCCUPANCY_LEVELS)[number]

/**
 * Rispetto a cosa è calcolato il livello: la capienza dichiarata, o — quando manca — il picco
 * abituale. Va mostrato accanto al livello: «molto affollata» rispetto ai posti e rispetto al
 * solito sono due frasi diverse.
 */
export type OccupancyLevelBasis = 'capacity' | 'usual_peak'

/** Cosa vedono gli iscritti dall'app (`GET /me/gym-occupancy`). Il pannello vede sempre tutto. */
export const OCCUPANCY_VISIBILITIES = ['hidden', 'level', 'count'] as const
export type OccupancyVisibility = (typeof OCCUPANCY_VISIBILITIES)[number]

/** L'affollamento di una sede adesso. */
export interface LocationOccupancy {
  /**
   * `null` è la voce dei passaggi che non nominano una sede: in un'organizzazione con più sedi
   * compare solo se oggi ce n'è stato almeno uno, in una senza sedi è l'unica voce.
   */
  locationId: Uuid | null
  locationName: string | null
  /** Il fuso in cui si legge «oggi» per `entriesToday` ed `exitsToday`. */
  timeZone: string
  /** Le persone dentro adesso, con l'uscita presunta già applicata. */
  inside: number
  capacity: number | null
  /**
   * `inside` diviso il riferimento del livello. **Può superare 1**: rispetto al picco abituale
   * vuol dire «più pieno del solito», rispetto alla capienza succede quando la si abbassa con
   * gente dentro.
   */
  ratio: number | null
  level: OccupancyLevel | null
  levelBasis: OccupancyLevelBasis | null
  /** La media oraria più alta delle ultime quattro settimane. `null` senza storia. */
  usualPeak: number | null
  /** La soglia applicata: quella della sede, o novanta minuti per i passaggi senza sede. */
  presumedExitAfterMinutes: number | null
  /** Entrati oggi senza un'uscita registrata e oltre la soglia: **non** contati in `inside`. */
  presumedOut: number
  /** Ingressi consentiti da mezzanotte, nel fuso della sede. */
  entriesToday: number
  /**
   * Uscite **registrate** da mezzanotte, non quelle dedotte dalla chiusura automatica. A zero
   * con molti ingressi dice che il varco non registra le uscite, e che `inside` è una stima.
   */
  exitsToday: number
}

/** `GET /organization/occupancy`: l'affollamento dell'organizzazione adesso. */
export interface OccupancySnapshot {
  /** L'istante a cui si riferisce il conteggio. */
  at: IsoDateTime
  /** La somma delle voci: il numero dell'organizzazione intera. */
  inside: number
  locations: LocationOccupancy[]
}

/** Una casella della settimana tipo: persone **mediamente presenti** in quell'ora. */
export interface OccupancyCell {
  /** ISO-8601: 1 = lunedì … 7 = domenica, come `OpeningHour.weekday`. */
  weekday: number
  hour: number
  average: number
}

/**
 * `GET /organization/occupancy/profile` — la settimana tipo e la giornata di oggi (`stats:read`).
 *
 * Misura chi **c'è**, non chi entra: ogni visita vale per i minuti che passa in ciascuna ora,
 * dall'ingresso all'uscita registrata o presunta. È la differenza con ANL-02, che conta gli
 * ingressi — alle 19 entrano meno persone che alle 18, ma in sala ce ne sono di più.
 */
export interface OccupancyProfile {
  locationId: Uuid | null
  locationName: string | null
  /** Il fuso delle ore: senza, «18:00» non significa niente. */
  timeZone: string
  weeks: number
  /** Il primo giorno contato. */
  from: IsoDate
  /** L'ultimo giorno contato, cioè ieri: un giorno a metà abbasserebbe la media. */
  to: IsoDate
  presumedExitAfterMinutes: number | null
  /** 168 caselle, **tutte presenti** anche a zero, da lunedì alle 00. */
  cells: OccupancyCell[]
  /** La casella più piena. `null` quando sono tutte a zero. */
  usualPeak: OccupancyCell | null
  /** La giornata in corso con la stessa misura: `null` per le ore che devono ancora venire. */
  today: { hour: number; average: number | null }[]
}

export interface GymMemberAccessCredential {
  gymMemberId: Uuid
  algorithm: string
  periodSeconds: number
  rotatedAt: string
  hasPrinted: boolean
  printedIssuedAt: string | null
  printedRevokedAt: string | null
  createdAt: string
}

/** Il segreto esce **una volta sola**, da qui: nessuna rotta lo rilegge. */
export interface GymMemberAccessCredentialSecret extends GymMemberAccessCredential {
  credentialRef: string
  secret: string
  provisioningUri: string
  toleranceSteps: number
}

export interface PrintedCredentialToken {
  gymMemberId: Uuid
  printedToken: string
  issuedAt: string
}

export interface GymMemberClaimCode {
  code: string
  expiresAt: string
}

// ── import ed export dell'anagrafica (ANA-07, ANA-08) ─────────────────────────

/**
 * Una riga del file da importare, **già interpretata dal pannello**.
 *
 * Il CSV non arriva al server: il file lo legge il browser. La ragione principale è che un
 * CSV di anagrafiche va **mappato** — la colonna «Cod.Fisc.» di un gestionale e «codice
 * fiscale» di un altro sono lo stesso campo, e chi lo sa è l'operatore davanti
 * all'anteprima, non il server che indovina. La seconda è che un file di anagrafiche è un
 * blocco di dati personali: non caricarlo significa non averne copia da nessuna parte.
 *
 * I campi sono stringhe **larghe**: il codice fiscale minuscolo lo normalizza il server, e
 * una data malformata fa fallire *quella riga* invece dell'intera richiesta.
 */
export interface GymMemberImportRow {
  /** Numero di riga nel file, per il report. Senza, «riga 3» non è la riga del CSV. */
  line?: number
  memberNumber?: string
  firstName: string
  lastName: string
  bornOn?: string
  birthPlace?: string
  birthProvince?: string
  birthCountryCode?: string
  sex?: Sex
  taxCode?: string
  email?: string
  phone?: string
  addressLine?: string
  postalCode?: string
  city?: string
  province?: string
  countryCode?: string
  internalNotes?: string
}

/** `skipped` è una riga già presente con `onDuplicate: 'skip'`: non è un errore. */
export type GymMemberImportOutcome = 'created' | 'updated' | 'skipped' | 'failed'

export interface GymMemberImportRowResult {
  index: number
  line: number | null
  outcome: GymMemberImportOutcome
  /** `null` su una riga fallita e su una che un `dryRun` avrebbe creato. */
  gymMemberId: Uuid | null
  /** Il campo con cui la riga ha riconosciuto un'anagrafica esistente. */
  matchedBy: 'tax_code' | 'member_number' | null
  code: string | null
  message: string | null
  field: string | null
}

/**
 * ⚠️ **Una voce per ogni riga, non solo per gli errori.** Su un catalogo il dry-run è una
 * validazione; su un'anagrafica è una *revisione* — «queste dodici esistono già, sono le
 * persone che credo?» — e la schermata deve mostrarlo come tale.
 */
export interface GymMemberImportResult {
  dryRun: boolean
  created: number
  updated: number
  skipped: number
  failed: number
  rows: GymMemberImportRowResult[]
}

/** Tutti i dati conservati su una persona — art. 20 GDPR. */
export interface GymMemberPortabilityExport {
  generatedAt: string
  /** Il titolare del trattamento è la palestra, non Gymmi. */
  organizationName: string
  gymMember: GymMember
  contacts: GymMemberContact[]
  relations: GymMemberRelation[]
  /** Il registro **intero**, non l'ultima riga per tipo. */
  consents: GymMemberConsent[]
  sportCards: GymMemberSportCard[]
  subscriptions: GymSubscription[]
  accessEvents: AccessEvent[]
  /** Da quando sono inclusi gli accessi: dodici mesi, e il documento lo dichiara. */
  accessEventsFrom: string
}

// ── F3a: catalogo vendibile, vendite e cassa ──────────────────────────────────────────────────

export const PRODUCT_KINDS = [
  'subscription',
  'service',
  'course',
  'goods',
  'credit',
  'digital',
] as const
export type ProductKind = (typeof PRODUCT_KINDS)[number]

export const VAT_NATURES = ['taxable', 'exempt_art10', 'not_subject', 'out_of_scope'] as const
export type VatNature = (typeof VAT_NATURES)[number]

export interface Product {
  id: Uuid
  code: string | null
  name: string
  description: string | null
  kind: ProductKind
  vatRateBp: number
  vatNature: VatNature
  storeBilling: boolean
  subscriptionKind: GymSubscriptionKind | null
  durationDays: number | null
  entriesTotal: number | null
  accessWindow: GymSubscriptionAccessWindow[]
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

/** Riga usata sia dal catalogo sia dalla tendina di vendita. Il prezzo appartiene al listino. */
export interface ProductListItem {
  id: Uuid
  code: string | null
  name: string
  kind: ProductKind
  subscriptionKind: GymSubscriptionKind | null
  durationDays: number | null
  entriesTotal: number | null
  vatRateBp: number
  priceCents: number | null
  archived: boolean
  createdAt: IsoDateTime
}

export const PRICE_LIST_KINDS = ['base', 'promo', 'segment'] as const
export type PriceListKind = (typeof PRICE_LIST_KINDS)[number]

export interface PriceList {
  id: Uuid
  name: string
  kind: PriceListKind
  locationId: Uuid | null
  currency: string
  validFrom: string | null
  validTo: string | null
  priority: number
  archivedAt: IsoDateTime | null
  itemCount: number
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

export interface PriceListItem {
  priceListId: Uuid
  productId: Uuid
  productName: string
  productKind: ProductKind
  priceCents: number
  updatedAt: IsoDateTime
}

export const DISCOUNT_KINDS = ['percent', 'amount'] as const
export type DiscountKind = (typeof DISCOUNT_KINDS)[number]
export type DiscountAppliesTo = 'line' | 'sale'

export interface Discount {
  id: Uuid
  name: string
  code: string | null
  kind: DiscountKind
  percentBp: number | null
  amountCents: number | null
  appliesTo: DiscountAppliesTo
  validFrom: string | null
  validTo: string | null
  maxRedemptions: number | null
  perMemberLimit: number | null
  redemptionsUsed: number
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

export type SaleChannel = 'desk' | 'web' | 'app' | 'device'
export type PaymentKind = 'payment' | 'refund' | 'reversal'
export const PAYMENT_METHODS = [
  'cash',
  'card_present',
  'card_online',
  'bank_transfer',
  'sdd',
  'voucher',
  'cheque',
  'internal_credit',
  'store',
] as const
export type PaymentMethod = (typeof PAYMENT_METHODS)[number]
export type PaymentStatus = 'pending' | 'settled' | 'failed'
export type PaymentPurpose = 'payment' | 'advance' | 'deposit'

export interface SaleItem {
  id: Uuid
  position: number
  productId: Uuid | null
  productName: string
  productKind: ProductKind
  priceListId: Uuid | null
  discountId: Uuid | null
  quantity: number
  unitPriceCents: number
  discountCents: number
  vatRateBp: number
  vatNature: VatNature
  taxableCents: number
  vatCents: number
  lineTotalCents: number
  gymSubscriptionId: Uuid | null
  serviceStartsOn: string | null
  serviceEndsOn: string | null
}

export interface Payment {
  id: Uuid
  saleId: Uuid
  kind: PaymentKind
  method: PaymentMethod
  purpose: PaymentPurpose
  status: PaymentStatus
  amountCents: number
  currency: string
  receivedAt: IsoDateTime
  businessDate: string
  settledAt: IsoDateTime | null
  failedAt: IsoDateTime | null
  failureReason: string | null
  reversesPaymentId: Uuid | null
  cashSessionId: Uuid | null
  collectedByName: string | null
  note: string | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

export interface VatSummaryEntry {
  vatRateBp: number
  vatNature: VatNature
  grossCents: number
  taxableCents: number
  vatCents: number
}

export interface Sale {
  serviceBookingId?: Uuid | null

  id: Uuid
  gymMemberId: Uuid | null
  locationId: Uuid | null
  channel: SaleChannel
  soldAt: IsoDateTime
  totalCents: number
  paidCents: number
  outstandingCents: number
  currency: string
  soldByName: string | null
  note: string | null
  items: SaleItem[]
  payments: Payment[]
  vatSummary: VatSummaryEntry[]
  voidedAt: IsoDateTime | null
  voidReason: string | null
  voidedByName: string | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

/** Che documento — FAT-01. */
export type FiscalDocumentKind = 'receipt' | 'invoice' | 'credit_note'

/**
 * Un documento fiscale — FAT-01. **Immutabile**: non c'è una `PATCH`, e si rettifica con una
 * nota di credito.
 *
 * ⚠️ Non copia le righe della vendita: quelle stanno su `sale_items` e sono immutabili per
 * privilegio. Ciò che congela sono le **due identità** — cedente e cessionario —, perché una
 * palestra cambia sede e perché `sales` non porta l'identità dell'intestatario oltre l'id.
 */
/**
 * Un'identità **fotografata al momento dell'emissione** — cedente o cessionario.
 *
 * È l'unica cosa che il documento congela, perché è l'unica che cambia da sé: una palestra
 * cambia sede e ragione sociale, e la fattura di marzo deve continuare a dire l'indirizzo
 * di marzo.
 *
 * ⚠️ Il nome del cessionario **sopravvive all'anonimizzazione** dell'anagrafica (ANA-09):
 * la conservazione decennale è la base legittima, ed è la ragione per cui `Sale` non lo
 * porta e questo sì.
 */
export interface FiscalDocumentParty {
  name: string
  vatNumber: string | null
  taxCode: string | null
  addressLine: string | null
  postalCode: string | null
  city: string | null
  province: string | null
  countryCode: string
}

export interface FiscalDocument {
  id: Uuid
  kind: FiscalDocumentKind
  saleId: Uuid
  seriesCode: string
  year: number
  number: number
  fullNumber: string
  /** La data **fiscale**: un giorno, non un istante. */
  issueDate: string
  /** Quando è stato emesso davvero: si emette stamattina il documento di ieri. */
  issuedAt: IsoDateTime
  seller: FiscalDocumentParty
  /** `null` su una ricevuta senza intestatario — la bottiglia d'acqua al banco. Mai su una fattura. */
  customer: FiscalDocumentParty | null
  /**
   * L'anagrafica intestataria, quando c'è: il collegamento che `customer` non può dare,
   * perché quello è una fotografia e resta identico anche dopo l'anonimizzazione.
   */
  gymMemberId: Uuid | null
  taxableCents: number
  vatCents: number
  /** Il lordo. Su una nota di credito è il totale **stornato**, con segno positivo: il verso lo dice `kind`. */
  totalCents: number
  currency: string
  vatSummary: VatSummaryEntry[]
  /**
   * Le righe, che vengono da `sale_items` e non sono una copia.
   *
   * ⚠️ **Presenti sul dettaglio, assenti nell'elenco**: è la sola ragione per cui
   * `GET /organization/fiscal-documents/{id}` esiste accanto all'elenco.
   */
  items?: SaleItem[]
  correctsDocumentId: Uuid | null
  correctedByDocumentId: Uuid | null
  /** Il motivo dello storno, sulla **nota di credito** che lo dichiara — non sul documento rettificato. */
  correctionReason: string | null
  note: string | null
  issuedByName: string | null
  createdAt: IsoDateTime
}

/**
 * Una rata — LIS-04. **Un'aspettativa e non un incasso**: il denaro vive su `payments`, e
 * questa riga dice entro quando quella parte del totale dovrebbe essere coperta.
 *
 * `coveredCents` e `status` li **calcola il server** e non si ricalcolano qui: due client che
 * lo facessero divergerebbero, e la differenza si scoprirebbe al telefono con chi deve pagare.
 */
export interface SaleInstallment {
  position: number
  dueOn: string
  amountCents: number
  cumulativeCents: number
  coveredCents: number
  status: 'paid' | 'partial' | 'due' | 'overdue'
}

export interface SaleListItem {
  id: Uuid
  soldAt: IsoDateTime
  gymMemberId: Uuid | null
  gymMemberFirstName: string | null
  gymMemberLastName: string | null
  gymMemberPhone: string | null
  channel: SaleChannel
  itemCount: number
  summary: string
  totalCents: number
  paidCents: number
  outstandingCents: number
  currency: string
  soldByName: string | null
  voidedAt: IsoDateTime | null
}

export interface CashSessionMethodTotal {
  method: PaymentMethod
  netCents: number
  paymentCount: number
}

export interface CashSession {
  id: Uuid
  locationId: Uuid | null
  openedAt: IsoDateTime
  businessDate: string
  openingFloatCents: number
  openedByName: string | null
  closedAt: IsoDateTime | null
  closedByName: string | null
  countedCashCents: number | null
  expectedCashCents: number
  differenceCents: number | null
  totalsByMethod: CashSessionMethodTotal[]
  note: string | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

export type CashSessionListItem = Omit<
  CashSession,
  'totalsByMethod' | 'note' | 'createdAt' | 'updatedAt'
>

// ── F4: palinsesto corsi, prenotazioni e presenze ─────────────────────────────────────────────

export const COURSE_LEVELS = ['all', 'beginner', 'intermediate', 'advanced'] as const
export type CourseLevel = (typeof COURSE_LEVELS)[number]

export interface Course {
  id: Uuid
  name: string
  discipline: string
  description: string | null
  level: CourseLevel
  /** Default copiati sulla lezione al momento in cui nasce, non letti da lei ogni volta. */
  durationMinutes: number
  capacity: number
  facilityId: Uuid | null
  roomLabel: string | null
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

export type CourseSessionStatus = 'scheduled' | 'cancelled' | 'completed'
export type CourseSessionSource = 'manual' | 'recurrence'

export interface CourseSession {
  id: Uuid
  courseId: Uuid
  courseName: string
  discipline: string
  startsAt: IsoDateTime
  endsAt: IsoDateTime
  durationMinutes: number
  capacity: number
  facilityId: Uuid | null
  facilityName: string | null
  roomLabel: string | null
  instructorMemberId: Uuid | null
  instructorName: string | null
  source: CourseSessionSource
  recurrenceRuleId: Uuid | null
  /** Una lezione generata che è stata spostata o corretta a mano: la regola non la riscrive più. */
  recurrenceException: boolean
  scheduleClosureId: Uuid | null
  status: CourseSessionStatus
  cancellationReason: string | null
  attendanceClosedAt: IsoDateTime | null
  /**
   * Posti occupati — `draft.21`. È **lo stesso insieme** con cui il server decide se la
   * lezione è piena, assenti compresi: mostrarne un altro darebbe una griglia che dice
   * «18/20» su una lezione che rifiuta il diciannovesimo.
   */
  bookedCount: number
  waitlistCount: number
  note: string | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

export type CourseBookingStatus = 'booked' | 'waitlisted' | 'cancelled' | 'attended' | 'no_show'

export interface CourseBooking {
  id: Uuid
  courseSessionId: Uuid
  gymMemberId: Uuid
  gymMemberName: string
  status: CourseBookingStatus
  waitlistPosition: number | null
  bookedAt: IsoDateTime
  promotedAt: IsoDateTime | null
  cancelledAt: IsoDateTime | null
  cancellationReason: string | null
  /** Dichiarata dal server, non calcolata qui: dipende dal preavviso del diritto comprato. */
  lateCancellation: boolean
  checkedInAt: IsoDateTime | null
  entitlementId: Uuid | null
  noShowPenaltyCharged: number
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

export interface CourseAttendanceSummary {
  courseSessionId: Uuid
  closedAt: IsoDateTime
  attended: number
  noShow: number
  cancelled: number
  /** Ingressi tolti dai carnet chiudendo il foglio: è il numero da dire a voce, se qualcuno chiede. */
  penaltyEntriesCharged: number
}

export interface Facility {
  locationId?: Uuid | null

  id: Uuid
  name: string
  kind: 'room' | 'pool' | 'court' | 'bed' | 'station' | 'area' | 'other'
  capacity: number
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

/**
 * Una regola ricorrente del palinsesto — COR-02.
 *
 * **Una sola coppia giorno/ora**: «martedì e giovedì» sono due regole, perché possono divergere
 * per istruttore, durata o data di fine senza modificarsi a vicenda.
 *
 * ⚠️ Ogni lezione generata resta una **risorsa autonoma**: prenotazioni, presenze ed eccezioni
 * citano il suo id, non ricalcolano una data dalla regola al momento della lettura. Quindi
 * cambiare la regola non riscrive le lezioni già generate — `materializedThrough` dice fin dove
 * è arrivata.
 */
export interface CourseRecurrenceRule {
  id: Uuid
  courseId: Uuid
  courseName: string
  instructorMemberId: Uuid | null
  instructorName: string | null
  /** ISO-8601: 1 = lunedì … 7 = domenica. */
  weekday: number
  /** Ora **civile**: «le 18» restano le 18 anche quando cambia l'ora legale. */
  localStartTime: string
  timezone: string
  startsOn: string
  endsOn: string | null
  durationMinutes: number
  capacity: number
  facilityId: Uuid | null
  roomLabel: string | null
  status: 'active' | 'paused' | 'ended'
  generationHorizonDays: number
  /** Fin dove le lezioni sono già state create. Oltre, non esistono ancora. */
  materializedThrough: string | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

/**
 * Una chiusura del palinsesto — COR-02, COR-03.
 *
 * `facilityId: null` chiude l'**intera organizzazione**; con una sala chiude solo quella. Due
 * chiusure d'organizzazione non si sovrappongono, così la causa conservata su una lezione
 * annullata resta univoca.
 */
export interface ScheduleClosure {
  affectedServiceBookingCount?: number
  affectedAppointmentCount?: number

  id: Uuid
  facilityId: Uuid | null
  startsAt: IsoDateTime
  endsAt: IsoDateTime
  reason: string
  /** Lezioni che risultano **ancora** annullate da questa chiusura. */
  affectedSessionCount: number
  createdByMemberId: Uuid | null
  createdByName: string | null
  createdAt: IsoDateTime
}

// ── servizi con accesso dedicato (F4, SRV-01…04) ───────────────────────────────

export const SERVICE_KINDS = [
  'gym_floor',
  'pool',
  'sauna',
  'solarium',
  'court',
  'functional_area',
  'personal_training',
  'other',
] as const
export type ServiceKind = (typeof SERVICE_KINDS)[number]

/**
 * Come si accede a un servizio, ed è il campo che governa tutto il resto: solo gli
 * ultimi due si prenotano, e solo quelli che contengono `consumption` scalano un
 * carnet. La pagina ci ramifica sopra invece di offrire gesti che il server
 * rifiuterebbe.
 */
export const SERVICE_ACCESS_MODES = [
  'included',
  'consumption',
  'booking',
  'booking_and_consumption',
] as const
export type ServiceAccessMode = (typeof SERVICE_ACCESS_MODES)[number]

export interface Service {
  productId?: Uuid | null

  id: Uuid
  name: string
  description: string | null
  kind: ServiceKind
  accessMode: ServiceAccessMode
  slotMinutes: number | null
  capacity: number | null
  resourceCount?: number
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

export interface ServiceResource {
  locationId?: Uuid | null
  startStepMinutes?: number
  durationOptionsMinutes?: number[]

  id: Uuid
  serviceId: Uuid
  name: string
  slotMinutes: number
  facilityId: Uuid | null
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

/** Uno slot della griglia. `bookingId` è ciò che rende la disdetta un clic solo. */
export interface ServiceSlot {
  startsAt: IsoDateTime
  endsAt: IsoDateTime
  available: boolean
  bookingId: Uuid | null
}

export interface ServiceResourceAvailability {
  locationId?: Uuid | null
  locationName?: string | null
  timezone?: string
  startStepMinutes?: number
  durationOptionsMinutes?: number[]

  resourceId: Uuid
  resourceName: string
  slotMinutes: number
  slots: ServiceSlot[]
}

export type ServiceBookingStatus = 'booked' | 'cancelled' | 'completed' | 'no_show'

export interface ServiceBooking {
  saleId?: Uuid | null
  saleTotalCents?: number | null
  salePaidCents?: number | null
  saleOutstandingCents?: number | null
  saleVoidedAt?: string | null

  id: Uuid
  serviceId: Uuid
  serviceName: string
  resourceId: Uuid
  resourceName: string
  gymMemberId: Uuid
  gymMemberName: string
  gymSubscriptionId: Uuid | null
  /** `null` significa che nessun abbonamento la copre: al banco è ammesso, e va visto. */
  entitlementId: Uuid | null
  startsAt: IsoDateTime
  endsAt: IsoDateTime
  status: ServiceBookingStatus
  cancelledAt: IsoDateTime | null
  cancellationReason: string | null
  checkedInAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

export interface ServiceCreditWallet {
  id: Uuid
  gymSubscriptionId: Uuid
  serviceId: Uuid
  serviceName: string
  entitlementId: Uuid | null
  initialUnits: number
  remainingUnits: number
  subscriptionStatus: string
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

/**
 * Perché il saldo di un carnet è cambiato — SRV-04.
 *
 * `manual_adjustment` è la correzione di una persona e ha **sempre** una nota; gli altri
 * cinque sono fatti del sistema.
 */
export type ServiceCreditMovementReason =
  'grant' | 'booking' | 'cancellation' | 'check_in' | 'no_show' | 'manual_adjustment'

/**
 * Una riga del registro di un carnet — SRV-04. **Non si modifica.**
 *
 * `balanceAfter` è memorizzato e non ricalcolato, per la stessa ragione per cui la riga di
 * vendita conserva il prezzo: il saldo di allora è un fatto, e ricostruirlo sommando i delta
 * darebbe un numero diverso ogni volta che una regola cambia.
 */
export interface ServiceCreditMovement {
  id: Uuid
  walletId: Uuid
  serviceBookingId: Uuid | null
  /** Negativo consuma, positivo restituisce. **Mai zero**: un movimento che non muove niente non è un fatto. */
  deltaUnits: number
  balanceAfter: number
  reason: ServiceCreditMovementReason
  operatorName: string | null
  note: string | null
  occurredAt: IsoDateTime
}

/**
 * Cosa un prodotto concede su **un** servizio — SRV-02.
 *
 * ⚠️ `includedUnits` è la **dichiarazione**, non il saldo: è il numero da cui SRV-04 fa nascere
 * il carnet alla vendita. Il primo non cambia mai, il secondo cala a ogni ingresso, e
 * confonderli è il modo di far sembrare che il catalogo si consumi.
 */
export interface ProductServiceEntitlement {
  id: Uuid
  serviceId: Uuid
  serviceName: string
  /** `null` = senza limite di quantità, cioè la sala pesi inclusa. */
  includedUnits: number | null
  /** «Quattro al mese, per sempre» — un limite **diverso** da `includedUnits`, non una sua variante. */
  monthlyLimit: number | null
  createdAt: IsoDateTime
}

/**
 * Le regole di prenotazione che un prodotto concede su un corso — COR-05.
 *
 * `courseId: null` vale per **tutti i corsi**, e di righe così ce n'è al massimo una: ha senso
 * perché il palinsesto cambia ogni stagione, e un abbonamento che li includa tutti non va
 * riscritto a ogni corso nuovo.
 */
export interface ProductCourseEntitlement {
  id: Uuid
  courseId: Uuid | null
  courseName: string | null
  monthlyBookingLimit: number | null
  bookingOpensDays: number
  bookingClosesMinutes: number
  cancellationNoticeMinutes: number
  noShowPenaltyUnits: number
  createdAt: IsoDateTime
}

/**
 * Una fascia oraria in cui **questo abbonamento** consente un servizio — SRV-02.
 *
 * Ha la forma di `GymSubscriptionAccessWindow` e non la riusa, per differenza di significato:
 * là sono le ore in cui questa persona può **entrare**, qui quelle in cui le è concesso
 * **questo servizio**. Le due si sommano — la piscina aperta il pomeriggio e l'abbonamento
 * «open» del mattino non si incontrano mai. Elenco vuoto significa «le ore del servizio», non
 * «nessuna».
 */
export interface ServiceAccessWindow {
  weekday: number
  from: string
  to: string
}

/**
 * Cosa un'iscrizione **già venduta** concede su un servizio — SRV-02, fotografato alla vendita.
 *
 * ⚠️ Non è `ProductServiceEntitlement` con un altro nome: quello è il catalogo di **oggi**,
 * questo è ciò che quella persona ha comprato **allora**. Il rinnovo ricopia il catalogo
 * corrente, quindi due iscrizioni consecutive possono includere cose diverse — ed è giusto
 * così: nessuno ha comprato due volte la stessa promozione.
 *
 * `includedUnits` resta la **dichiarazione**, non il saldo: il carnet a scalare (SRV-04) è
 * un'altra riga, e confonderli fa sembrare che la fotografia si consumi.
 */
export interface GymSubscriptionServiceEntitlement {
  id: Uuid
  gymSubscriptionId: Uuid
  /** `null` se quel diritto è stato tolto dal prodotto: la fotografia resta, l'origine no. */
  sourceProductEntitlementId: Uuid | null
  serviceId: Uuid
  serviceName: string
  includedUnits: number | null
  monthlyLimit: number | null
  accessWindow: ServiceAccessWindow[]
  /** Quelli dell'**iscrizione**: un diritto non sopravvive all'abbonamento che l'ha portato. */
  validFrom: string
  validTo: string | null
  createdAt: IsoDateTime
}

/** Le regole di prenotazione fotografate alla vendita — COR-08. Vedi sopra per il perché di due tipi. */
export interface GymSubscriptionCourseEntitlement {
  id: Uuid
  gymSubscriptionId: Uuid
  sourceProductEntitlementId: Uuid | null
  /** `null` vale per **tutti i corsi**, e di righe così ce n'è al massimo una. */
  courseId: Uuid | null
  courseName: string | null
  monthlyBookingLimit: number | null
  bookingOpensDays: number
  bookingClosesMinutes: number
  cancellationNoticeMinutes: number
  noShowPenaltyUnits: number
  validFrom: string
  validTo: string | null
  createdAt: IsoDateTime
}

/**
 * Un ruolo personalizzato di un'organizzazione — OPS-03.
 *
 * L'**insieme** dei permessi lo decide la piattaforma — un permesso che il backend non
 * controlla non esiste —, le **combinazioni** le decide la palestra: «receptionist che può anche
 * registrare un pagamento» è legittima e non merita un ruolo predefinito in più.
 *
 * `roleId` e non `id`, come `memberId` e `invitationId` in quest'area.
 */
export interface CustomOrgRole {
  roleId: Uuid
  /** Identificatore stabile e **non modificabile**: si cambia eliminando e ricreando. */
  key: string
  label: string
  basedOn: OrgRole
  permissions: string[]
  memberCount: number
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

export interface OrgRoleListResponse {
  data: CustomOrgRole[]
  /**
   * Il catalogo dei permessi che il backend controlla **davvero**, cioè le caselle che ha senso
   * disegnare. Viaggia con i ruoli perché la sola fonte di verità è il codice che li applica: un
   * elenco ricopiato nel client diverge alla prima versione, e il sintomo sarebbe una casella
   * che non concede nulla — un permesso che chi amministra crede di aver dato.
   */
  availablePermissions: string[]
}

// ── anagrafica dell'organizzazione e sedi (contract `1.0.0-draft.27`) ──────────

/**
 * L'indirizzo, con la stessa forma per l'organizzazione e per la sede.
 *
 * Ogni campo è annullabile perché lo è nel contract: un'organizzazione `personal`
 * non ha una via, e pretenderla renderebbe impossibile salvare il resto.
 */
export interface Address {
  line: string | null
  postalCode: string | null
  city: string | null
  province: string | null
  /** ISO 3166-1 alpha-2 maiuscolo. Il server ha `IT` come predefinito. */
  countryCode: string
}

/**
 * Una fascia di apertura di una sede.
 *
 * `weekday` è **ISO-8601** — 1 = lunedì, 7 = domenica — e non l'indice 0-based di
 * JavaScript. La differenza costa un giorno intero di sfasamento e non dà errori:
 * gli orari finiscono semplicemente sulla colonna sbagliata.
 */
export interface OpeningHour {
  weekday: number
  /** `HH:MM` nel fuso **della sede**, non in UTC. */
  opensAt: string
  closesAt: string
}

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'suspended' | 'cancelled'

/**
 * L'anagrafica del tenant — `GET /organization`.
 *
 * `plan`, `subscriptionStatus` e `disabled` si leggono e **non** si scrivono: sono
 * dell'abbonamento alla piattaforma, e una palestra che potesse scriversi il piano
 * non avrebbe un piano. Il pannello li mostra perché spiegano un `403`
 * `organization_suspended` che altrimenti sembrerebbe un guasto.
 */
export interface Organization {
  id: Uuid
  kind: OrganizationKind
  name: string
  legalName: string | null
  slug: string | null
  vatNumber: string | null
  taxCode: string | null
  address: Address
  email: string | null
  phone: string | null
  website: string | null
  logoUrl: string | null
  plan: string
  subscriptionStatus: SubscriptionStatus
  subscriptionExpiresAt?: IsoDateTime | null
  disabled: boolean
  createdAt: IsoDateTime
  updatedAt?: IsoDateTime
}

/**
 * Una sede — `GET /organization/locations`.
 *
 * `capacity` è ciò che il motore d'accesso legge per `location_full`, e `timezone`
 * è della sede e non dell'organizzazione: una catena può averne in fusi diversi.
 */
export interface Location {
  id: Uuid
  organizationId: Uuid
  name: string
  address: Address
  phone: string | null
  email: string | null
  timezone: string
  capacity: number | null
  /** Elenco completo delle fasce, non una per giorno. Vuoto = orari non dichiarati. */
  openingHours: OpeningHour[]
  /**
   * ACC-07: dopo quanti minuti senza un'uscita registrata chi è entrato **smette di
   * contare** — per la presenza, per l'affollamento e per `location_full`. Da 15 a 720;
   * `null` è «mai», cioè dentro fino alla chiusura automatica di fine giornata. Il server
   * parte da 90, il caso di chi legge il QR solo all'ingresso.
   */
  presumedExitAfterMinutes: number | null
  /** ACC-07: cosa vedono gli iscritti dall'app. Il server parte da `count`. */
  occupancyVisibility: OccupancyVisibility
  /** Archiviazione soft: una sede chiusa conserva accessi, iscrizioni e vendite. */
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt?: IsoDateTime
}

// ── F5: modelli di scheda e schede assegnate (contract `1.0.0-draft.29`) ───────

export const TRAINING_GOALS = [
  'strength',
  'hypertrophy',
  'weight_loss',
  'endurance',
  'mobility',
  'rehabilitation',
  'general',
] as const
export type TrainingGoal = (typeof TRAINING_GOALS)[number]

export const TRAINING_ASSIGNMENT_STATUSES = ['active', 'completed', 'cancelled'] as const
export type TrainingAssignmentStatus = (typeof TRAINING_ASSIGNMENT_STATUSES)[number]

/**
 * Una riga di programma.
 *
 * Somiglia a `CardExercise` e non è la stessa cosa: quella appartiene alla scheda
 * di un **utente**, questa a un giorno di un programma — di un modello o di una
 * scheda assegnata a un iscritto che un account non ce l'ha.
 */
export interface TrainingExercise {
  id: Uuid
  position: number
  exerciseId: ExerciseId
  exerciseName: string
  sets: number
  reps: string
  weight: string | null
  restSeconds: number | null
  /** Cadenza in notazione da palestra, `3-1-1-0`. Testo libero come `reps`. */
  tempo: string | null
  note: string | null
}

/**
 * Un giorno del programma.
 *
 * `weekNumber` e `dayNumber` sono numeri di **programma** e non date: «settimana 2,
 * giorno 3» resta vero per chi comincia lunedì e per chi comincia il giovedì dopo.
 */
export interface TrainingDay {
  id: Uuid
  weekNumber: number
  dayNumber: number
  label: string | null
  note: string | null
  exercises: TrainingExercise[]
}

/** Un modello della libreria di organizzazione — TRN-02. */
export interface WorkoutTemplate {
  id: Uuid
  name: string
  goal: TrainingGoal | null
  level: ExerciseLevel | null
  note: string | null
  weeks: number | null
  dayCount: number
  exerciseCount: number
  createdByMemberId: Uuid | null
  createdByName: string | null
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
  /** Solo sul dettaglio: l'elenco porta i conteggi e non la struttura. */
  days?: TrainingDay[]
}

/**
 * Una scheda assegnata a un iscritto — TRN-01.
 *
 * `templateId` dice **da dove viene**, non è un collegamento vivo: la struttura è
 * una fotografia presa alla consegna, e il modello di domani non la tocca.
 */
export interface TrainingAssignment {
  id: Uuid
  gymMemberId: Uuid
  gymMemberName: string
  templateId: Uuid | null
  templateName: string | null
  name: string
  goal: TrainingGoal | null
  note: string | null
  status: TrainingAssignmentStatus
  /** `YYYY-MM-DD`: una data di calendario, non un istante. */
  startsOn: string
  endsOn: string | null
  assignedByMemberId: Uuid | null
  assignedByName: string | null
  dayCount: number
  exerciseCount: number
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
  days?: TrainingDay[]
}

/**
 * Un punto dell'andamento di un esercizio — TRN-15.
 *
 * Un punto è una **sessione chiusa**, non un giorno: due allenamenti nello stesso giorno sono
 * due fatti, e fonderli mostrerebbe una media che nessuno ha eseguito.
 *
 * ⚠️ `bestWeightKg` e `volumeKg` sono `null` su un esercizio **senza carico** — un plank, una
 * trazione a corpo libero. Non zero: un grafico che li disegnasse a zero direbbe «non è
 * migliorato» di chi ha raddoppiato le ripetizioni.
 */
export interface ExerciseProgressPoint {
  sessionId: Uuid
  /** `YYYY-MM-DD`. */
  date: string
  setCount: number
  bestWeightKg: number | null
  bestReps: number | null
  volumeKg: number | null
}

export interface ExerciseProgress {
  exerciseId: ExerciseId
  exerciseName: string
  /** Dal più vecchio al più recente: l'ordine in cui si disegna. */
  points: ExerciseProgressPoint[]
}

/** Riga in scrittura: con `id` conserva l'identità, senza `id` è nuova. */
export interface TrainingExerciseInput {
  id?: Uuid
  exerciseId: ExerciseId
  sets?: number
  reps?: string
  weight?: string | null
  restSeconds?: number | null
  tempo?: string | null
  note?: string | null
}

export interface TrainingDayInput {
  id?: Uuid
  weekNumber?: number
  dayNumber: number
  label?: string | null
  note?: string | null
  exercises: TrainingExerciseInput[]
}

// ── attrezzature per sede (F5, TRN-07, contract `1.0.0-draft.33`) ─────────────

export const EQUIPMENT_KINDS = [
  'machine',
  'free_weight',
  'cardio',
  'functional',
  'accessory',
  'other',
] as const
export type EquipmentKind = (typeof EQUIPMENT_KINDS)[number]

/**
 * Un attrezzo di una sede.
 *
 * `locationId` è annullabile: una palestra con una sede sola non ha motivo di
 * ripeterla. `exerciseIds` è ciò che rende TRN-05 possibile — il Coach restringe
 * il catalogo del prompt a quello che quella sede può eseguire.
 */
export interface Equipment {
  id: Uuid
  locationId: Uuid | null
  locationName: string | null
  name: string
  kind: EquipmentKind
  quantity: number
  note: string | null
  exerciseIds: ExerciseId[]
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

// ── piani alimentari e alimenti (F5, NUT-01/04/05, contract `1.0.0-draft.35`) ──

export const MEAL_PLAN_STATUSES = ['draft', 'active', 'completed', 'cancelled'] as const
export type MealPlanStatus = (typeof MEAL_PLAN_STATUSES)[number]

/**
 * Un alimento con i suoi valori — NUT-04.
 *
 * `organizationId` a `null` è una riga **di piattaforma**: si legge da ogni palestra
 * e non si modifica da nessuna. Provarci è un `409 platform_food_read_only`, non un
 * `404` — la riga esiste e si cita nei piani — né un `403`, perché chi chiede ha il
 * permesso. I valori sono **per 100 g**.
 */
export interface ExternalFoodProduct {
  barcode: string
  source: 'open_food_facts'
  sourceUrl: string
  license: 'ODbL-1.0'
  basis: '100g' | '100ml'
  brand: string | null
  nutrients: {
    kcal: number | null
    proteinG: number | null
    carbsG: number | null
    fatG: number | null
    fiberG: number | null
    sugarsG: number | null
    saturatedFatG: number | null
    saltG: number | null
  }
}
export interface Food {
  externalProduct?: ExternalFoodProduct | null
  id: Uuid
  organizationId: Uuid | null
  name: string
  category: string | null
  kcal: number | null
  proteinG: number | null
  carbsG: number | null
  fatG: number | null
  fiberG: number | null
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

/**
 * I totali di un pasto o di un giorno, calcolati dal **server**.
 *
 * `unmeasuredItems` conta le righe escluse — senza grammi, o con un alimento dai
 * valori incompleti — e va mostrato con il totale: 1 800 kcal su un giorno di cui
 * metà non è misurabile non sono 1 800 kcal, sono il conto di una metà.
 */
export interface MealPlanTotals {
  kcal: number
  proteinG: number
  carbsG: number
  fatG: number
  unmeasuredItems: number
}

export interface MealPlanItemAlternative {
  id: Uuid
  foodId: Uuid | null
  foodName: string
  quantityG: number | null
  quantityText: string | null
  note: string | null
}

/** Le alternative stanno **dentro** la riga a cui si alternano, e non nei totali. */
export interface MealPlanItem extends MealPlanItemAlternative {
  alternatives: MealPlanItemAlternative[]
}

export interface MealPlanMeal {
  id: Uuid
  name: string
  /** `HH:MM`, ora civile: «colazione alle 8» non cambia significato a Bolzano. */
  timeOfDay: string | null
  note: string | null
  items: MealPlanItem[]
  totals: MealPlanTotals
}

export interface MealPlanDay {
  id: Uuid
  /** Da 1 a 7, e **non** una data: un piano si scrive «il lunedì». */
  dayNumber: number
  label: string | null
  note: string | null
  meals: MealPlanMeal[]
  totals: MealPlanTotals
}

/**
 * Un piano alimentare — NUT-01 — oppure un **modello** della libreria — NUT-05.
 *
 * Li distingue `gymMemberId`: senza è un modello, con è il piano di una persona. È
 * la stessa entità, a differenza delle schede che sono due: là l'assegnazione porta
 * stato, aderenza e diario, qui le due forme coincidono. Assegnare **copia**.
 */
export interface MealPlan {
  id: Uuid
  gymMemberId: Uuid | null
  gymMemberName: string | null
  /** Provenienza, e la riporta solo la risposta della creazione. */
  templateId: Uuid | null
  name: string
  goal: string | null
  note: string | null
  status: MealPlanStatus
  startsOn: string | null
  endsOn: string | null
  /** Chi **firma** il piano (NUT-07): `null` è la bozza che nessuno ha firmato. */
  professionalMemberId: Uuid | null
  professionalName: string | null
  dailyKcalTarget: number | null
  dayCount: number
  mealCount: number
  itemCount: number
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
  days?: MealPlanDay[]
}

export interface MealPlanItemAlternativeInput {
  id?: Uuid
  foodId?: Uuid
  foodName?: string
  quantityG?: number | null
  quantityText?: string | null
  note?: string | null
}

export interface MealPlanItemInput extends MealPlanItemAlternativeInput {
  alternatives?: MealPlanItemAlternativeInput[]
}

export interface MealPlanMealInput {
  id?: Uuid
  name: string
  timeOfDay?: string | null
  note?: string | null
  items: MealPlanItemInput[]
}

export interface MealPlanDayInput {
  id?: Uuid
  dayNumber: number
  label?: string | null
  note?: string | null
  meals: MealPlanMealInput[]
}

// ── documenti assegnati (F5, TRN-04 e NUT-02, contract `1.0.0-draft.37`) ───────

export const MEMBER_DOCUMENT_KINDS = ['workout_plan', 'meal_plan', 'other'] as const
export type MemberDocumentKind = (typeof MEMBER_DOCUMENT_KINDS)[number]

/**
 * Un documento assegnato a un iscritto.
 *
 * `kind` non è un'etichetta: da esso dipende il **permesso**, perché un documento è
 * la cosa che rappresenta — una scheda in PDF è una scheda. Per la stessa ragione
 * non si modifica: chi ha sbagliato tipo cancella e ricarica.
 *
 * I byte non ci sono: si scaricano da `/content`. `contentType` è quello **reale**,
 * dedotto dai byte al caricamento.
 */
export interface MemberDocument {
  id: Uuid
  gymMemberId: Uuid
  kind: MemberDocumentKind
  title: string
  contentType: string
  sizeBytes: number
  /** Se l'iscritto lo vede nell'app. `false` è l'appunto interno. */
  visibleToMember: boolean
  uploadedByName: string | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

// ── CRM: i contatti (F6, CRM-01, contract `1.0.0-draft.38`) ───────────────────

export const LEAD_SOURCES = [
  'walk_in',
  'web_form',
  'referral',
  'social',
  'campaign',
  'phone',
  'other',
] as const
export type LeadSource = (typeof LEAD_SOURCES)[number]

/**
 * Una riga della lista di contatti da importare — CRM-02.
 *
 * ⚠️ I formati sono **più larghi** di `CreateLeadInput`: l'email arriva com'è scritta nel file
 * e la normalizza il server, perché rifiutare `MARIO.ROSSI@LIBERO.IT ` significherebbe far
 * correggere a mano duecento righe che sono giuste.
 */
export interface LeadImportRow {
  /** La riga **nel file**, non nell'array: è quella che l'operatore ha davanti. */
  line?: number
  firstName: string
  lastName: string
  email?: string
  phone?: string
  /** Sovrascrive quella della richiesta, per la riga che arriva da un'altra parte. */
  source?: LeadSource
  note?: string
}

export interface LeadImportInput {
  leads: LeadImportRow[]
  /** L'origine della lista: obbligatoria, come sulla creazione singola. */
  source: LeadSource
  dryRun?: boolean
  /** Riguarda i **lead** esistenti. Gli iscritti restano fuori con qualunque valore. */
  onDuplicate?: 'skip' | 'update'
  assignedMemberId?: Uuid | null
  /** Vale solo sui contatti **creati**: non sposta la data di chi è già in trattativa. */
  nextActionAt?: string | null
}

export interface LeadImportRowResult {
  index: number
  line: number | null
  outcome: 'created' | 'updated' | 'skipped' | 'failed'
  /** `null` su una riga fallita e su una che un `dryRun` avrebbe creato. */
  leadId: Uuid | null
  /**
   * Valorizzato **solo** con `code: 'alreadyMember'`: è l'iscritto che la riga ha riconosciuto,
   * e la risposta alla domanda che segue subito («chi è?»).
   */
  gymMemberId: Uuid | null
  matchedBy: 'email' | 'phone' | null
  /** `validation_failed`, `conflict` su `failed`; `duplicate`, `alreadyMember` su `skipped`. */
  code: string | null
  message: string | null
  field: string | null
}

export interface LeadImportResult {
  dryRun: boolean
  created: number
  updated: number
  skipped: number
  failed: number
  rows: LeadImportRowResult[]
}

export const LEAD_STATUSES = [
  'new',
  'contacted',
  'trial_booked',
  'negotiating',
  'won',
  'lost',
] as const
export type LeadStatus = (typeof LEAD_STATUSES)[number]

/**
 * Un contatto da lavorare — CRM-01.
 *
 * Non è un `GymMember` con un campo in più: un iscritto ha tesseramenti,
 * abbonamenti, accessi e consensi, un lead nessuna di queste cose. `gymMemberId`
 * dice che è **diventato** qualcuno, e il lead resta perché è la storia di come
 * quella persona è arrivata.
 */
export interface Lead {
  id: Uuid
  firstName: string
  lastName: string
  email: string | null
  phone: string | null
  source: LeadSource
  status: LeadStatus
  /** Manuale, non calcolato: un punteggio automatico senza dati finge da misura. */
  score: number | null
  assignedMemberId: Uuid | null
  assignedName: string | null
  /** `YYYY-MM-DD`. È il campo su cui l'elenco ordina. */
  nextActionAt: string | null
  nextActionNote: string | null
  note: string | null
  lostReason: string | null
  gymMemberId: Uuid | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

// ── CRM: note, attività e segmenti (F6, CRM-08 e CRM-04) ──────────────────────

/**
 * Una nota o un'attività — CRM-08.
 *
 * Le distingue `dueOn`: senza è ciò che è stato detto, con è ciò che va fatto entro
 * quel giorno. `done` è un booleano perché chi chiude non decide *quando*: l'istante
 * lo mette il server.
 */
export interface CrmTask {
  id: Uuid
  leadId: Uuid | null
  gymMemberId: Uuid | null
  subjectName: string
  assignedMemberId: Uuid
  assignedName: string | null
  createdByName: string | null
  body: string
  dueOn: string | null
  done: boolean
  doneAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

export const SEGMENT_KEYS = ['expired', 'inactive', 'birthdays', 'never_booked_course'] as const
export type SegmentKey = (typeof SEGMENT_KEYS)[number]

/** Un segmento con quante persone contiene. I nomi stanno dietro un altro permesso. */
export interface Segment {
  key: SegmentKey
  label: string
  description: string
  count: number
}

/** Una persona di un segmento, **con il fatto che la mette lì**. */
export interface SegmentMember {
  gymMemberId: Uuid
  firstName: string
  lastName: string
  email: string | null
  phone: string | null
  expiredOn?: string | null
  lastAccessAt?: IsoDateTime | null
  bornOn?: string | null
}

/**
 * Un fatto nella cronologia di una persona — CRM-07, contract `1.0.0-draft.49`.
 *
 * `kind` è una **stringa aperta** e non un'unione chiusa, ed è deliberato: le fonti entrano
 * una alla volta, e un `switch` esaustivo si romperebbe alla prossima. Per ciò che non si
 * riconosce si mostra `title`, che è scritto per essere letto così.
 *
 * L'evento **non cambia mai**: il server non accetta correzioni su questa tabella, quindi
 * una riga già letta si può conservare senza chiedersi se sia ancora valida.
 */
export interface CustomerEvent {
  id: Uuid
  /** **Quando è successo**, non quando è stato scritto. */
  occurredAt: IsoDateTime
  kind: string
  title: string
  detail?: Record<string, unknown>
  sourceType?: string | null
  sourceId?: Uuid | null
}

export interface TimelinePage {
  data: CustomerEvent[]
  meta: {
    nextCursor: string | null
    /**
     * Da quando la palestra registra la cronologia. **Prima di questo istante non c'è
     * silenzio: c'è assenza di registrazione**, e la schermata deve dire quale dei due
     * sta mostrando — altrimenti una scheda vuota fa credere che prima non sia successo
     * niente.
     */
    recordingSince: IsoDateTime | null
  }
}

/** Lo stato di una campagna — CRM-03. **Non c'è `failed`**: ciò che fallisce è un invio. */
export const CAMPAIGN_STATUSES = ['queued', 'running', 'done', 'canceled'] as const
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number]

/**
 * Un invio a un segmento — CRM-03, contract `1.0.0-draft.48`.
 *
 * `sent`, `skipped` e `failed` li conta il server su `messages`: non sono colonne che
 * qualcuno incrementa, quindi non possono divergere da ciò che lo storico di ogni
 * persona dice. `totalRecipients` è un'istantanea presa all'inizio, ed è `null` finché
 * la campagna è `queued` — prima di partire non c'è un istante da fotografare.
 */
export interface MessageCampaign {
  id: Uuid
  segmentKey: SegmentKey
  segmentLabel: string
  templateId: Uuid | null
  subject: string
  body: string
  status: CampaignStatus
  totalRecipients: number | null
  sent: number
  skipped: number
  failed: number
  startedAt: IsoDateTime | null
  finishedAt: IsoDateTime | null
  createdByName: string | null
  createdAt: IsoDateTime
}

/**
 * Cosa è stato mandato a chi — CRM-03.
 *
 * Il testo è **copiato e non riferito**: correggere il modello di gennaio non riscrive ciò che
 * una persona ha ricevuto a marzo. Vale per `subject`, `body` e `recipient` — l'indirizzo è
 * quello **al momento dell'invio**, così «l'avete mandato al vecchio indirizzo?» ha risposta.
 */
export interface Message {
  id: Uuid
  templateId: Uuid | null
  campaignId: Uuid | null
  gymMemberId: Uuid | null
  leadId: Uuid | null
  subjectName: string
  channel: 'email' | 'push'
  kind: 'marketing' | 'service'
  recipient: string
  subject: string
  body: string
  /**
   * ⚠️ `skipped` è **diverso da `failed`**: il primo è «non gliel'abbiamo mandato perché non
   * vuole», il secondo è «abbiamo provato e non è partito». Fonderli farebbe sembrare un guasto
   * ciò che è una scelta della persona.
   */
  status: 'sent' | 'failed' | 'skipped'
  failureReason: string | null
  sentByName: string | null
  createdAt: IsoDateTime
}

export interface MessageTemplate {
  id: Uuid
  key: string
  name: string
  kind: 'marketing' | 'service'
  subject: string
  body: string
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

// ── automazioni (F6, CRM-05) ──────────────────────────────────────────────────

export const AUTOMATION_OUTCOMES = ['pending', 'done', 'skipped', 'failed'] as const
export type AutomationOutcome = (typeof AUTOMATION_OUTCOMES)[number]

/**
 * Un'**occorrenza** di un'automazione: una regola diventata vera per qualcuno, e cosa ne è
 * seguito.
 *
 * `ruleId` è una **stringa aperta e non un'enumerazione**, e il tipo lo rispetta: le regole
 * entrano una alla volta, e un pannello che facesse `switch` su tutti i valori si romperebbe
 * alla prossima. Per ciò che non riconosce si mostra `ruleLabel`, che il server manda già in
 * parole **con la soglia dentro** — «Abbonamento in scadenza fra 7 giorni» —, perché quella
 * soglia cambierà e un log che dicesse solo «scadenza» costringerebbe a indovinare quale
 * fosse allora.
 */
export interface AutomationRun {
  id: Uuid
  ruleId: string
  ruleLabel: string
  subjectType: 'gym_member'
  subjectId: Uuid
  subjectName: string
  /** Chiave di idempotenza scelta dalla regola: **opaca**, non è una data anche quando lo sembra. */
  firedFor: string
  /** Quale valore ha reso vera la condizione: la metà della risposta a «perché è partita?». */
  matchedValue: string | null
  action: 'message' | 'task' | 'tag'
  outcome: AutomationOutcome
  /** C'è solo con `action: 'message'`: da lì si legge **cosa** ha ricevuto la persona. */
  messageId: Uuid | null
  detail: Record<string, unknown>
  createdAt: IsoDateTime
}

/**
 * Una regola del motore **come vale adesso** in questa palestra — CRM-06.
 *
 * Non è la riga di una tabella di regole: quella tabella non esiste. Il catalogo è nel codice del
 * server, e qui si legge cosa ne è stato cambiato — `configured` dice quale dei due valori sta
 * rispondendo, cioè se «torna ai valori di serie» cambierebbe qualcosa.
 *
 * `action` decide **quali campi sono modificabili**: `subject` e `body` esistono solo per
 * `message`, e su una regola `task` il server risponde `422` invece di accettarli e ignorarli.
 */
export interface AutomationRule {
  ruleId: string
  /** La regola in parole, **con la soglia attiva dentro**: la compone il server, non il pannello. */
  ruleLabel: string
  action: 'message' | 'task' | 'tag'
  enabled: boolean
  configured: boolean
  thresholdDays: number | null
  defaultThresholdDays: number | null
  subject: string | null
  body: string | null
  defaultSubject: string | null
  defaultBody: string | null
  /** I segnaposto che **questa** regola sa sostituire: uno sconosciuto è `422`. */
  variables: string[]
  updatedAt: IsoDateTime | null
}

// ── agenda (F6, AGD-01/02) ────────────────────────────────────────────────────

export const APPOINTMENT_KINDS = [
  'personal_training',
  'trial',
  'medical_check',
  'nutrition_consult',
  'measurements',
  'other',
] as const
export type AppointmentKind = (typeof APPOINTMENT_KINDS)[number]

export const APPOINTMENT_STATUSES = [
  'scheduled',
  'confirmed',
  'done',
  'no_show',
  'cancelled',
] as const
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number]

export interface Appointment {
  id: Uuid
  kind: AppointmentKind
  status: AppointmentStatus
  professionalMemberId: Uuid | null
  professionalName: string | null
  gymMemberId: Uuid | null
  leadId: Uuid | null
  subjectName: string
  /** C'è perché il gesto che segue un «non si è presentato» è una telefonata. */
  subjectPhone: string | null
  locationId: Uuid | null
  facilityId: Uuid | null
  startsAt: IsoDateTime
  endsAt: IsoDateTime
  notes: string | null
  cancelReason: string | null
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

export interface WorkHoursBand {
  /** ISO: 1 è lunedì, come le ricorrenze dei corsi. */
  weekday: number
  startsAtTime: string
  endsAtTime: string
  locationId: Uuid | null
}

export interface TimeOff {
  id: Uuid
  startsAt: IsoDateTime
  endsAt: IsoDateTime
  reason: string | null
  createdAt: IsoDateTime
}

export interface AvailabilitySlot {
  startsAt: IsoDateTime
  endsAt: IsoDateTime
}

// ── analisi e statistiche (F7, ANL-00…06) ────────────────────────────────────

/**
 * La finestra come il server l'ha intesa. **Torna con ogni analisi** perché è la riga che va scritta
 * sopra un grafico: un'immagine salvata senza il suo periodo non è più vera domani.
 */
export interface AnalyticsWindow {
  from: IsoDate
  to: IsoDate
  /** Giorni **compresi gli estremi**: dal 1 al 31 gennaio sono trentuno. */
  days: number
}

/** Un gruppo con il suo conteggio. `label` viaggia col gruppo: una fascia ha un criterio dentro. */
export interface AnalyticsBand {
  key: string
  label: string
  count: number
}

/** Come sopra, ma per il denaro. Due tipi e non uno con un campo opzionale: vedi il contract. */
export interface AnalyticsAmountBand {
  key: string
  label: string
  amountCents: number
}

export interface MemberAnalytics {
  window: AnalyticsWindow
  headcount: {
    atStart: number
    atEnd: number
    joined: number
    ended: number
    /** `ended / atStart`, **zero e non assente** quando all'inizio non c'era nessuno. */
    churnRate: number
  }
  composition: {
    byAgeBand: AnalyticsBand[]
    bySex: AnalyticsBand[]
    byTenureBand: AnalyticsBand[]
  }
  /** `null` quando non si è conclusa nessuna iscrizione: una media di zero elementi non è zero. */
  averageMembershipDays: number | null
}

export interface AccessAnalytics {
  window: AnalyticsWindow
  /** Il fuso in cui le ore sono state contate: senza, «18:00» non significa niente. */
  timeZone: string
  total: number
  distinctMembers: number
  averageVisitsPerMember: number | null
  byHour: AnalyticsBand[]
  byWeekday: AnalyticsBand[]
  peak: { weekday: number; hour: number; count: number } | null
  /** Iscritti senza un ingresso da trenta giorni. **Non dipende dalla finestra.** */
  inactiveMembers: number
  byService: AnalyticsBand[]
}

export interface CourseAnalytics {
  window: AnalyticsWindow
  sessions: number
  cancelledSessions: number
  capacity: number
  seatsTaken: number
  fillRate: number | null
  cancellations: number
  noShows: number
  waitlisted: number
  byDiscipline: AnalyticsBand[]
  byInstructor: AnalyticsBand[]
}

export interface RevenueAnalytics {
  window: AnalyticsWindow
  /** **Può essere negativo**: un periodo in cui si è rimborsato più di quanto si è incassato esiste. */
  netCents: number
  previous: { from: IsoDate; to: IsoDate; netCents: number }
  /** `null` quando prima non è entrato niente: una crescita infinita non è un numero da mostrare. */
  changeRatio: number | null
  byMethod: AnalyticsAmountBand[]
  /** **Venduto** e non incassato: un pagamento copre il documento intero. */
  byProduct: AnalyticsAmountBand[]
  byOperator: AnalyticsAmountBand[]
  unpaidCents: number
}

export interface BillingAnalytics {
  window: AnalyticsWindow
  taxableCents: number
  vatCents: number
  grossCents: number
  /** La parte del venduto la cui competenza cade **dopo** la finestra. */
  deferredCents: number
  averagePerMemberCents: number | null
  byVatRate: AnalyticsAmountBand[]
  byVatNature: AnalyticsAmountBand[]
}

export interface TrainingAnalytics {
  window: AnalyticsWindow
  assigned: number
  membersWithPlan: number
  completedSessions: number
  /** La media **per scheda** delle aderenze di TRN-05. `null` se nessuna ha un previsto. */
  averageAdherence: number | null
  topExercises: AnalyticsBand[]
}

/**
 * Cosa dice il **diario alimentare** dei piani consegnati — ANL-07, contract `1.0.0-draft.80`.
 *
 * ⚠️ **Non si chiama «aderenza», e non va mostrato come se lo fosse.** `loggedDays / coveredDays`
 * misura quanto il diario viene **compilato**: chi registra una pizza ogni sera è al cento per
 * cento. La schermata scrive «Giorni compilati», non «Aderenza», e la differenza è tutta lì.
 */
export interface NutritionDiaryStats {
  /** Coppie (persona, giorno) coperte da un piano consegnato. Il denominatore. */
  coveredDays: number
  loggedDays: number
  membersLogging: number
  /** Su quanti giorni è calcolata `averageLoggedKcal`. Va mostrato accanto a quella media. */
  fullyMeasuredDays: number
  entries: number
  /** `null` quando nessun giorno è interamente misurato: uno zero direbbe «non mangiano». */
  averageLoggedKcal: number | null
}

export interface NutritionAnalytics {
  window: AnalyticsWindow
  assigned: number
  membersWithPlan: number
  /** Un obiettivo **scritto** dal nutrizionista, non un consumo misurato. */
  averageDailyKcalTarget: number | null
  diary: NutritionDiaryStats
  byStatus: AnalyticsBand[]
  byGoal: AnalyticsBand[]
  byProfessional: AnalyticsBand[]
}

/** Un report salvato — ANL-08. È un **elenco di filtri con un nome**, non un'istantanea di numeri. */
export interface AnalyticsReport {
  id: Uuid
  name: string
  area: 'members' | 'access' | 'courses' | 'revenue' | 'billing' | 'training' | 'nutrition'
  /**
   * **Il modo di calcolare il periodo, non il periodo.** Solo `custom` porta due date: un report con
   * «dal 1° al 31 gennaio» dentro è un'immagine, e un'immagine si salva con uno screenshot.
   */
  windowKind: 'last_30_days' | 'last_90_days' | 'year_to_date' | 'previous_month' | 'custom'
  from: IsoDate | null
  to: IsoDate | null
  locationId: Uuid | null
  schedule: 'none' | 'weekly' | 'monthly'
  recipientEmail: string | null
  /** L'ultimo invio: è anche la difesa contro il doppio invio, e sta nel database. */
  lastSentOn: IsoDate | null
  createdByName: string | null
  createdAt: IsoDateTime
}

export interface RunReportResult {
  report: AnalyticsReport
  window: AnalyticsWindow
  /** Il corpo dell'analisi dell'area, **identico** a quello della sua rotta. */
  analytics: Record<string, unknown>
}

// ── magazzino (F8, INV-01…04) ────────────────────────────────────────────────

/** Un fornitore — INV-01. Anagrafica del listino d'**acquisto**. */
export interface Supplier {
  id: Uuid
  name: string
  email: string | null
  phone: string | null
  note: string | null
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
}

/**
 * Un articolo di magazzino — INV-01.
 *
 * **È un prodotto visto dal lato della merce**: nome, prezzo di vendita e IVA stanno sul prodotto
 * (F3a), qui ci sono barcode, scorta minima, prezzo d'acquisto, fornitore e giacenza per sede.
 */
export interface InventoryItem {
  id: Uuid
  productId: Uuid
  productName: string
  barcode: string | null
  category: string | null
  supplierId: Uuid | null
  supplierName: string | null
  purchasePriceCents: number | null
  minStock: number
  stock: { locationId: Uuid; locationName: string; quantity: number }[]
  /** **Può essere negativo**: è il dato che dice «il magazzino non è aggiornato». */
  totalStock: number
  belowMinStock: boolean
  archivedAt: IsoDateTime | null
  createdAt: IsoDateTime
}

/** Un movimento — INV-02. Il registro è append-only: una correzione è una rettifica. */
export interface InventoryMovement {
  id: Uuid
  inventoryItemId: Uuid
  productName: string
  locationId: Uuid
  locationName: string | null
  kind: 'load' | 'unload' | 'adjust' | 'transfer_in' | 'transfer_out' | 'sale'
  /** Con il segno: è il numero che si somma. */
  delta: number
  /** La giacenza di quella sede **dopo**, fotografata. */
  quantityAfter: number
  countedQuantity: number | null
  reason: string | null
  transferGroupId: Uuid | null
  saleId: Uuid | null
  createdByName: string | null
  occurredAt: IsoDateTime
  createdAt: IsoDateTime
}

// ── ordini dal web (F8, SHOP-01) ───────────────────────────────────────────────

/**
 * Lo stato di un ordine dal web — SHOP-01.
 *
 * ⚠️ Manca `ready` deliberatamente: uno stato che qualcuno deve ricordarsi di cliccare è uno stato
 * che dopo due settimane non corrisponde a niente, e il suo unico scopo sarebbe avvisare il cliente
 * — cioè un'email, che in produzione oggi non esce.
 */
export type ShopOrderStatus = 'placed' | 'collected' | 'cancelled' | 'expired'

/** Una riga d'ordine: **il prodotto come era il giorno dell'ordine**, nome e prezzo congelati. */
export interface ShopOrderItem {
  position: number
  productId: Uuid
  name: string
  quantity: number
  unitPriceCents: number
  lineTotalCents: number
}

/**
 * Un ordine dal web — SHOP-01, e **non una vendita**.
 *
 * Una vendita è denaro entrato e merce uscita; un ordine è una persona che ha detto «lo prendo» e
 * che deve ancora presentarsi. Al ritiro l'ordine **diventa** una vendita con `channel: 'web'`, e
 * `saleId` la nomina.
 */
export interface ShopOrder {
  id: Uuid
  /** Il riferimento che il cliente porta al banco. Unico nell'organizzazione, non oltre. */
  code: string
  status: ShopOrderStatus
  customerFirstName: string
  customerLastName: string
  customerEmail: string | null
  customerPhone: string | null
  /** L'iscritto riconosciuto **al ritiro**: `null` finché nessuno l'ha ritirato. */
  gymMemberId: Uuid | null
  note: string | null
  totalCents: number
  currency: string
  placedAt: IsoDateTime
  /** L'ultimo giorno utile per ritirare. Sette giorni dall'ordine, non configurabile. */
  expiresOn: IsoDate
  collectedAt: IsoDateTime | null
  saleId: Uuid | null
  cancelledAt: IsoDateTime | null
  cancelReason: string | null
  items: ShopOrderItem[]
  createdAt: IsoDateTime
  updatedAt: IsoDateTime
}

/** La riga d'elenco: nome e codice, che sono le due cose che si dicono a voce al banco. */
export interface ShopOrderListItem {
  id: Uuid
  code: string
  status: ShopOrderStatus
  customerFirstName: string
  customerLastName: string
  /** Nell'elenco e non solo nel dettaglio: con un ordine fermo da sei giorni si telefona. */
  customerPhone: string | null
  totalCents: number
  currency: string
  placedAt: IsoDateTime
  expiresOn: IsoDate
  itemCount: number
}

// ── borsellino dell'iscritto (F3a, PAY-07) ─────────────────────────────────────

/**
 * Il borsellino di un iscritto — PAY-07. **Denaro già incassato**, non credito concesso.
 *
 * La ricarica è una vendita come le altre; spendere il credito **non è** un incasso, perché la somma
 * è entrata una volta sola alla ricarica. È la ragione per cui `internal_credit` non compare nella
 * quadratura di cassa.
 */
export interface MemberWallet {
  gymMemberId: Uuid
  /** **Non può essere negativo**: un saldo negativo sarebbe credito concesso, cioè un insoluto. */
  balanceCents: number
  currency: string
  /** `null` su un borsellino mai usato: «non ha credito» è una risposta, non un errore. */
  updatedAt: IsoDateTime | null
}

/**
 * `refund` e `reversal` sono due fatti diversi e non un tipo con due segni: «gli è stato
 * restituito» e «la ricarica non è mai valsa» non sono la stessa cosa da spiegare a una persona.
 */
export type WalletMovementKind = 'topup' | 'spend' | 'refund' | 'reversal'

/** Una riga del registro — PAY-07. **Immutabile**: una correzione è un movimento nuovo. */
export interface WalletMovement {
  id: Uuid
  kind: WalletMovementKind
  /** Con il segno: è il numero che si somma, e non si ricava da `kind`. */
  deltaCents: number
  /** Il saldo **dopo**, fotografato: «−15» sei mesi dopo non dice se ce n'erano venti o duecento. */
  balanceAfterCents: number
  saleId: Uuid | null
  reason: string | null
  createdByName: string | null
  occurredAt: IsoDateTime
  createdAt: IsoDateTime
}
